/**
 * --------------------------------------------------------------------------
 * Shopify Cart Buffer (Highly Responsive Cart State Manager)
 * --------------------------------------------------------------------------
 * Provides a local cart buffer for instant UI updates, background sync,
 * multi-tab sync, and robust error/retry handling. Attaches to window.Shopify.cart.
 *
 * Features:
 * - Debounced add operations: Rapid successive clicks are batched and accumulated
 * - Optimistic UI updates for immediate feedback
 * - Background sync with Shopify API
 * - Multi-tab synchronization
 * - Offline support with retry queue
 * - Progressive enhancement (works without JS)
 * - Cart state visibility controls (empty/full/filling states)
 * - Automatic checkout disabling when cart doesn't meet minimum requirements
 *
 * Usage:
 *   window.Shopify.cart.add({ id, quantity, properties })
 *   window.Shopify.cart.update({ updates })
 *   window.Shopify.cart.change({ id, quantity, properties })
 *   window.Shopify.cart.remove(id)
 *   window.Shopify.cart.clear()
 *   window.Shopify.cart.get() // returns current buffer state
 *   window.Shopify.cart.flushPendingAdds() // force process pending debounced adds
 *   window.Shopify.cart.flushPendingChanges() // force process pending debounced changes
 *   window.Shopify.cart.flushPendingOperations() // force process all pending operations
 *   window.Shopify.cart.forceSyncWithShopify() // force sync with Shopify cart
 *   window.Shopify.cart.ensureInitialized() // ensure cart is properly initialized
 *   window.Shopify.cart.clearPendingForItem(id) // clear pending debounced operations for specific item
 *   window.Shopify.cart.updateCartStateVisibility() // manually update cart state visibility
 *   window.Shopify.cart.getCartState() // get current cart state information
 *
 * Listen for events:
 *   window.addEventListener('cart:updated', (e) => { ... })
 *   window.addEventListener('cart:error', (e) => { ... })
 *   window.addEventListener('cart:checkout-state-changed', (e) => { ... }) // Emitted when checkout state changes
 *
 * Cart State Visibility:
 *   Add data-cart-state="state:action" to elements (e.g., "empty:show", "full:hide", "minimum:show")
 *   - empty: Shows when cart has no items
 *   - full: Shows when cart reaches capacity limit (if looping disabled)
 *   - empty: Shows when cart has items but isn't full
 *   - minimum: Shows when cart meets minimum threshold (use data-cart-minimum="X" to set threshold)
 *   - show: Element is visible when condition is true
 *   - hide: Element is hidden when condition is true
 *
 * Checkout Disabling:
 *   Automatically disables checkout forms, buttons, and links when cart doesn't meet minimum requirements.
 *   Minimum threshold is controlled by cart_min_items_per_box theme setting.
 *   Disabled elements get opacity-50, pointer-events-none, cursor-not-allowed classes and
 *   data-checkout-disabled="true" attribute with helpful title tooltip.
 *
 * Fallback: If JS is disabled, forms submit as normal.
 */

(function () {
  const CART_STORAGE_KEY = "shopify-cart-buffer";
  const MAX_RETRIES = 5;
  const RETRY_BASE_DELAY = 1000; // ms

  // Helper: Deep clone
  function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  // Helper: Get initial cart from window.Shopify._initialCart or window.Shopify.cart if available (set in theme.liquid)
  // This is the most up-to-date cart data from server-side rendering
  // Note: window.Shopify.cart is overwritten by the optimistic cart API, so we check _initialCart first
  function getInitialCartFromWindow() {
    try {
      // Priority 1: Check _initialCart (stored before optimistic cart overwrites window.Shopify.cart)
      if (window.Shopify && window.Shopify._initialCart) {
        const cart = window.Shopify._initialCart;
        // Validate cart format - must have items array (matches Shopify cart API format)
        if (cart && Array.isArray(cart.items)) {
          return deepClone(cart);
        }
      }
      
      // Priority 2: Fallback to window.Shopify.cart (in case it's still the initial data)
      // This handles edge cases where the script loads before optimistic cart attaches
      if (window.Shopify && window.Shopify.cart) {
        const cart = window.Shopify.cart;
        // Only use if it's NOT the optimistic cart API object (which has methods like add, update, etc.)
        if (cart && Array.isArray(cart.items) && typeof cart.add !== 'function') {
          return deepClone(cart);
        }
      }
    } catch (e) {
      // Silently fail if window.Shopify.cart is malformed or not available
    }
    return null;
  }

  // Helper: Deep equality for properties
  function deepEqual(a, b) {
    if (a === b) return true;
    if (typeof a !== "object" || typeof b !== "object" || !a || !b)
      return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (!bKeys.includes(key) || !deepEqual(a[key], b[key])) return false;
    }
    return true;
  }

  // Helper: Get current time in specific timezone (without shifting)
  function getCurrentTimeInTimezone(timezone) {
    try {
      // Get the current time in the target timezone without shifting
      // This gives us the actual local time in that timezone
      const now = new Date();
      const targetTimeString = now.toLocaleString("en-US", { timeZone: timezone });
      return new Date(targetTimeString);
    } catch (e) {
      // Fallback to current time if timezone conversion fails
      return new Date();
    }
  }

  // Helper: Format date with strftime-like format
  function formatDateWithStrftime(date, format) {
    if (!date) return '';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    return format.replace(/%([aAbBcdeHImMnOpPrRsStTuUVwWxXyYzZ])/g, (match, code) => {
      switch (code) {
        case 'a': return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
        case 'A': return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()];
        case 'b': return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
        case 'B': return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][d.getMonth()];
        case 'c': return d.toLocaleString();
        case 'd': return d.getDate().toString().padStart(2, '0');
        case 'e': return d.getDate().toString();
        case 'H': return d.getHours().toString().padStart(2, '0');
        case 'I': return (d.getHours() % 12 || 12).toString().padStart(2, '0');
        case 'm': return (d.getMonth() + 1).toString().padStart(2, '0');
        case 'M': return d.getMinutes().toString().padStart(2, '0');
        case 'n': return '\n';
        case 'p': return d.getHours() < 12 ? 'AM' : 'PM';
        case 'P': return d.getHours() < 12 ? 'am' : 'pm';
        case 'r': return d.toLocaleTimeString();
        case 'R': return d.toTimeString().slice(0, 5);
        case 'S': 
          // Return ordinal suffix (st, nd, rd, th) instead of seconds
          const day = d.getDate();
          if (day >= 11 && day <= 13) return 'th';
          switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
          }
        case 't': return '\t';
        case 'T': return d.toTimeString();
        case 'u': return (d.getDay() || 7).toString();
        case 'U': return Math.ceil((d - new Date(d.getFullYear(), 0, 1)) / 604800000).toString();
        case 'V': return Math.ceil((d - new Date(d.getFullYear(), 0, 1)) / 604800000).toString();
        case 'w': return d.getDay().toString();
        case 'W': return Math.ceil((d - new Date(d.getFullYear(), 0, 1)) / 604800000).toString();
        case 'x': return d.toLocaleDateString();
        case 'X': return d.toLocaleTimeString();
        case 'y': return d.getFullYear().toString().slice(-2);
        case 'Y': return d.getFullYear().toString();
        case 'z': return d.getTimezoneOffset() ? `GMT${d.getTimezoneOffset() > 0 ? '-' : '+'}${Math.abs(d.getTimezoneOffset() / 60)}` : 'GMT';
        case 'Z': return d.getTimezoneOffset() ? `GMT${d.getTimezoneOffset() > 0 ? '-' : '+'}${Math.abs(d.getTimezoneOffset() / 60)}` : 'GMT';
        default: return match;
      }
    });
  }

  // Helper: Update cart attribute values
  function updateCartAttributeValues() {
    try {

      
      const elements = document.querySelectorAll('[data-cart-attribute-value]');
      
      if (elements.length) {
        // Log timezone info for debugging
        const globalSettings = window.Shopify?.theme?.settings;
        if (globalSettings) {
          const timezone = globalSettings.cart_timezone || 'Europe/London';
          const nowInTimezone = getCurrentTimeInTimezone(timezone);
  
        }
        
        // Add loading styles if not already added
        if (!document.getElementById('cart-attribute-loading-styles')) {
          const style = document.createElement('style');
          style.id = 'cart-attribute-loading-styles';
          style.textContent = `
            [data-cart-attribute-value].cart-attribute-loading {
              opacity: 0.6;
              transition: opacity 0.3s ease;
            }
            [data-cart-attribute-value]:not(.cart-attribute-loading) {
              opacity: 1;
              transition: opacity 0.3s ease;
            }
          `;
          document.head.appendChild(style);
        }
        
        elements.forEach((el, index) => {
          // Add loading class initially
          el.classList.add('cart-attribute-loading');
          
          const which = el.getAttribute('data-which') || 'next';
          const format = el.getAttribute('data-format') || '%A %e%S %B';
          
          let date = null;
          
          // Calculate dates fresh every time - no caching
          if (globalSettings) {
            const deliveryDay = globalSettings.cart_delivery_day !== undefined ? parseInt(globalSettings.cart_delivery_day) : undefined;
            const cutoffDay = globalSettings.cart_cutoff_day !== undefined ? parseInt(globalSettings.cart_cutoff_day) : undefined;
            const cutoffHour = globalSettings.cart_cutoff_hour !== undefined ? parseInt(globalSettings.cart_cutoff_hour) : 0;
            const cutoffMinute = globalSettings.cart_cutoff_minute !== undefined ? parseInt(globalSettings.cart_cutoff_minute) : 0;
            const cutoffSecond = globalSettings.cart_cutoff_second !== undefined ? parseInt(globalSettings.cart_cutoff_second) : 0;
            const cutoffMillisecond = globalSettings.cart_cutoff_millisecond !== undefined ? parseInt(globalSettings.cart_cutoff_millisecond) : 0;
            
            if (deliveryDay !== undefined) {
              // Get current time in the configured timezone
              const timezone = globalSettings.cart_timezone || 'Europe/London';
              
              // Get current time in the target timezone (this is the actual local time there)
              const nowInTimezone = getCurrentTimeInTimezone(timezone);
              const currentDay = nowInTimezone.getDay();
              
              // Calculate next delivery date
              const daysUntilDelivery = (deliveryDay - currentDay + 7) % 7;
              const nextDelivery = new Date(nowInTimezone);
              nextDelivery.setDate(nowInTimezone.getDate() + daysUntilDelivery);
              nextDelivery.setHours(0, 0, 0, 0);
              
              if (which === 'cutoff' && cutoffDay !== undefined) {
                // Calculate cutoff date fresh
                const cutoff = new Date(nextDelivery);
                const daysBack = (deliveryDay - cutoffDay + 7) % 7;
                cutoff.setDate(nextDelivery.getDate() - daysBack);
                cutoff.setHours(cutoffHour, cutoffMinute, cutoffSecond, cutoffMillisecond);
                
                // Check if current cutoff has passed, if so, calculate next cutoff
                if (nowInTimezone >= cutoff) {
                  // Current cutoff has passed, calculate next cutoff
                  const nextCutoff = new Date(cutoff);
                  nextCutoff.setDate(cutoff.getDate() + 7); // Next week
                  nextCutoff.setHours(cutoffHour, cutoffMinute, cutoffSecond, cutoffMillisecond);
                  
                  // Also calculate next delivery for this cutoff
                  const nextDeliveryForCutoff = new Date(nextCutoff);
                  const daysToDelivery = (deliveryDay - cutoffDay + 7) % 7;
                  nextDeliveryForCutoff.setDate(nextCutoff.getDate() + daysToDelivery);
                  nextDeliveryForCutoff.setHours(0, 0, 0, 0);
                  
                  // Update the next delivery date for future calculations
                  nextDelivery.setTime(nextDeliveryForCutoff.getTime());
                  
                  date = nextCutoff;
                } else {
                  date = cutoff;
                }
              } else if (which === 'next') {
                // Check if current cutoff has passed, if so, calculate next delivery
                if (cutoffDay !== undefined) {
                  const cutoff = new Date(nextDelivery);
                  const daysBack = (deliveryDay - cutoffDay + 7) % 7;
                  cutoff.setDate(nextDelivery.getDate() - daysBack);
                  cutoff.setHours(cutoffHour, cutoffMinute, cutoffSecond, cutoffMillisecond);
                  
                  if (nowInTimezone >= cutoff) {
                    // Current cutoff has passed, calculate next delivery
                    const nextCutoff = new Date(cutoff);
                    nextCutoff.setDate(cutoff.getDate() + 7); // Next week
                    nextCutoff.setHours(cutoffHour, cutoffMinute, cutoffSecond, cutoffMillisecond);
                    
                    const nextDeliveryForCutoff = new Date(nextCutoff);
                    const daysToDelivery = (deliveryDay - cutoffDay + 7) % 7;
                    nextDeliveryForCutoff.setDate(nextCutoff.getDate() + daysToDelivery);
                    nextDeliveryForCutoff.setHours(0, 0, 0, 0);
                    
                    date = nextDeliveryForCutoff;
                  } else {
                    date = nextDelivery;
                  }
                } else {
                  date = nextDelivery;
                }
              }
            }
          }
          
          if (date) {
            let formatted = '';
            
            // Try to use the delivery-date component's formatter if available
            const deliveryDateEl = document.querySelector('delivery-date');
            if (deliveryDateEl && typeof deliveryDateEl.formatDateWithStrftime === 'function') {
              try {
                formatted = deliveryDateEl.formatDateWithStrftime(new Date(date), format);
              } catch (err) {
                // Error using delivery-date formatter, falling back to built-in
                formatted = formatDateWithStrftime(new Date(date), format);
              }
            } else {
              // Use our built-in strftime formatter
              formatted = formatDateWithStrftime(new Date(date), format);
            }
            
            // Only update if we got a valid formatted string
            if (formatted && formatted.trim()) {
              el.textContent = formatted;
              // Remove loading class once formatted
              el.classList.remove('cart-attribute-loading');
            } else {
              // Got empty formatted string for date
              // Keep loading class if formatting failed
            }
          } else {
            // No date available for cart attribute
            // Keep loading class if no date available
          }
        });
      }
      
      // Also update cart state visibility when cart attribute values are updated
      if (window.Shopify?.cart?._cart) {
        updateCartStateVisibility(window.Shopify.cart._cart);
      }
      
      // Also update checkout element states when cart attribute values are updated
      // This ensures checkout elements are properly disabled/enabled on page load
      if (window.Shopify?.cart?._cart) {
        const cartState = getCartState(window.Shopify.cart._cart);
        updateCheckoutElementStates(cartState);
      }
    } catch (err) {
      // Error in updateCartAttributeValues
      console.error('Error in updateCartAttributeValues:', err);
    }
  }

  // Helper: Check if cutoff has passed and schedule next update
  function scheduleNextCartAttributeUpdate() {
    try {
      const globalSettings = window.Shopify?.theme?.settings;
      if (!globalSettings) return;
      
      const deliveryDay = globalSettings.cart_delivery_day !== undefined ? parseInt(globalSettings.cart_delivery_day) : undefined;
      const cutoffDay = globalSettings.cart_cutoff_day !== undefined ? parseInt(globalSettings.cart_cutoff_day) : undefined;
      
      if (deliveryDay === undefined || cutoffDay === undefined) return;
      
      // Get current time in the configured timezone
      const timezone = globalSettings.cart_timezone || 'Europe/London';
      
      // Get current time in the target timezone (this is the actual local time there)
      const nowInTimezone = getCurrentTimeInTimezone(timezone);
      
      // Calculate current cutoff time
      const currentDay = nowInTimezone.getDay();
      const daysUntilDelivery = (deliveryDay - currentDay + 7) % 7;
      const nextDelivery = new Date(nowInTimezone);
      nextDelivery.setDate(nowInTimezone.getDate() + daysUntilDelivery);
      nextDelivery.setHours(0, 0, 0, 0);
      
      const cutoff = new Date(nextDelivery);
      const daysBack = (deliveryDay - cutoffDay + 7) % 7;
      cutoff.setDate(nextDelivery.getDate() - daysBack);
      
      const cutoffHour = globalSettings.cart_cutoff_hour !== undefined ? parseInt(globalSettings.cart_cutoff_hour) : 0;
      const cutoffMinute = globalSettings.cart_cutoff_minute !== undefined ? parseInt(globalSettings.cart_cutoff_minute) : 0;
      const cutoffSecond = globalSettings.cart_cutoff_second !== undefined ? parseInt(globalSettings.cart_cutoff_second) : 0;
      const cutoffMillisecond = globalSettings.cart_cutoff_millisecond !== undefined ? parseInt(globalSettings.cart_cutoff_millisecond) : 0;
      
      cutoff.setHours(cutoffHour, cutoffMinute, cutoffSecond, cutoffMillisecond);
      
      // Calculate time until next update
      let timeUntilUpdate = 0;
      
      if (nowInTimezone < cutoff) {
        // Cutoff hasn't passed yet, schedule update for when it does
        timeUntilUpdate = cutoff.getTime() - nowInTimezone.getTime();
      } else {
        // Cutoff has passed, schedule update for next cutoff (1 week later)
        const nextCutoff = new Date(cutoff);
        nextCutoff.setDate(cutoff.getDate() + 7);
        timeUntilUpdate = nextCutoff.getTime() - nowInTimezone.getTime();
      }
      
      // Add a small buffer (1 second) to ensure we update after the cutoff
      timeUntilUpdate += 1000;
      
      // Clear any existing timer
      if (window.Shopify?.cart?._cartAttributeUpdateTimer) {
        clearTimeout(window.Shopify.cart._cartAttributeUpdateTimer);
      }
      
      // Schedule the next update
      const nextUpdateTime = new Date(Date.now() + timeUntilUpdate);

      
      window.Shopify.cart._cartAttributeUpdateTimer = setTimeout(() => {

        // Update cart attribute values
        updateCartAttributeValues();
        
        // Schedule the next update
        scheduleNextCartAttributeUpdate();
      }, timeUntilUpdate);
      
    } catch (err) {
      console.error('Error scheduling next cart attribute update:', err);
    }
  }



  // Helper: Emit custom event
  function emit(event, detail) {
    window.dispatchEvent(new CustomEvent(event, { detail }));
    // Mirror cart attribute derived dates into text blocks, if present
    if (event === 'cart:updated') {
      try {
        // Cart attribute values are now updated directly in updateCartAttributeValues
        // No need to handle them here since we removed caching
        
        // Also update cart state visibility when cart:updated is emitted
        if (window.Shopify?.cart?._cart) {
          updateCartStateVisibility(window.Shopify.cart._cart);
        }
      } catch (e) {}
    }
  }

  // Helper: Format money using centralized utility (converts cents to pounds)
  function formatMoney(cents) {
    const pounds = cents / 100;
    // Use centralized money formatter if available, fallback to simple format
    if (window.theme?.utils?.money) {
      return window.theme.utils.money(pounds);
    }
    return "£" + pounds.toFixed(2);
  }

  // Helper: Update all cart total elements
  function updateCartTotalElements(cart, optimistic = true) {
    const totalCents = cart.total_price || 0;
    const originalTotalCents = cart.original_total_price || 0;
    const hasDiscount = originalTotalCents > totalCents;

    // Update elements with data-cart-total attribute (simple total)
    const totalElements = document.querySelectorAll("[data-cart-total]");
    totalElements.forEach((element) => {
      const shouldUpdateOptimistically =
        element.getAttribute("data-update-optimistic") !== "false";
      if (optimistic === shouldUpdateOptimistically) {
        element.textContent = formatMoney(totalCents);
      }
    });

    // Update elements with data-cart-total-with-original attribute (total with strikethrough original when discount exists)
    const totalWithOriginalElements = document.querySelectorAll("[data-cart-total-with-original]");
    totalWithOriginalElements.forEach((element) => {
      const shouldUpdateOptimistically =
        element.getAttribute("data-update-optimistic") !== "false";
      if (optimistic === shouldUpdateOptimistically) {
        if (hasDiscount) {
          element.innerHTML = formatMoney(totalCents) + 
            ' <span class="line-through font-normal">' + 
            formatMoney(originalTotalCents) + 
            '</span>';
        } else {
          element.textContent = formatMoney(totalCents);
        }
      }
    });
  }

  // Helper: Update all cart discount total elements
  function updateCartDiscountTotalElements(cart, optimistic = true) {
    const discountCents = cart.total_discount || 0;

    // Update elements with data-cart-discount-total attribute based on optimistic setting
    const discountElements = document.querySelectorAll("[data-cart-discount-total]");
    discountElements.forEach((element) => {
      const shouldUpdateOptimistically =
        element.getAttribute("data-update-optimistic") !== "false";
      if (optimistic === shouldUpdateOptimistically) {
        // Check if parent has show_prefix setting or if we should infer from existing content
        const parent = element.closest('[data-show-prefix]');
        const showPrefix = parent 
          ? parent.getAttribute("data-show-prefix") === "true"
          : element.textContent.trim().startsWith("-");
        
        const formattedDiscount = (showPrefix ? "-" : "") + formatMoney(discountCents);
        element.textContent = formattedDiscount;
      }
    });
  }

  // Helper: Update all cart savings elements
  function updateCartSavingsElements(cart, optimistic = true) {
    const discountCents = cart.total_discount || 0;

    // Update elements with data-cart-savings attribute based on optimistic setting
    const savingsElements = document.querySelectorAll("[data-cart-savings]");
    savingsElements.forEach((element) => {
      const shouldUpdateOptimistically =
        element.getAttribute("data-update-optimistic") !== "false";
      if (optimistic === shouldUpdateOptimistically) {
        const prefix = element.getAttribute("data-savings-prefix");
        // Add space between prefix and value, unless prefix is '-' or ends with '-'
        const separator = (prefix === '-' || prefix.endsWith('-')) ? '' : ' ';
        const formattedSavings = discountCents > 0 ? prefix + separator + formatMoney(discountCents) : "";
        element.textContent = formattedSavings;
      }
    });
  }

  // Helper: Update all cart count elements
  function updateCartCountElements(cart, optimistic = true) {
    const itemCount = cart.item_count || 0;

    // Update elements with data-cart-count attribute based on optimistic setting
    const countElements = document.querySelectorAll("[data-cart-count]");
    countElements.forEach((element) => {
      const shouldUpdateOptimistically =
        element.getAttribute("data-update-optimistic") !== "false";
      if (optimistic === shouldUpdateOptimistically) {
        element.textContent = itemCount.toString();
      }
    });
  }

  // Helper: Extract all discount codes from cart (only discount_code type, not automatic/script discounts)
  // Returns array of objects with { title, dirty } instead of just strings
  // Uses cart_level_discount_applications as primary source to maintain original case and dirty flag
  function extractCartDiscountCodes(cart) {
    if (!cart) return [];
    
    const codesMap = new Map(); // Use Map to track by title (case-insensitive key) with full object
    
    // CRITICAL: Only extract codes from actual discount APPLICATIONS where they're applied
    // Do NOT use cart.discount_codes as primary source - it includes rejected codes with applicable:false
    // This matches the logic in discount-code-form.liquid (lines 24-38)
    
    // Primary source: cart_level_discount_applications (filter for type === 'discount_code')
    // This maintains the original case from the title field (e.g., "Yang testing" not "YANG TESTING")
    // and preserves the dirty flag for optimistic rendering
    if (Array.isArray(cart.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach(da => {
        if (da && da.type === 'discount_code' && typeof da.title === 'string') {
          const key = da.title.toLowerCase();
          if (!codesMap.has(key)) {
            codesMap.set(key, {
              title: da.title,
              dirty: da.dirty === true  // Preserve dirty flag (optimistic codes)
            });
          }
        }
      });
    }
    
    // Fallback: discount_applications (filter for type === 'discount_code')
    // These won't have dirty flag (server-confirmed)
    if (Array.isArray(cart.discount_applications)) {
      cart.discount_applications.forEach(da => {
        if (da && da.type === 'discount_code' && typeof da.title === 'string') {
          const key = da.title.toLowerCase();
          if (!codesMap.has(key)) {
            codesMap.set(key, {
              title: da.title,
              dirty: false
            });
          }
        }
      });
    }
    
    // Fallback: line-level discount allocations (filter for type === 'discount_code')
    // These won't have dirty flag (server-confirmed)
    if (Array.isArray(cart.items)) {
      cart.items.forEach(item => {
        if (Array.isArray(item.line_level_discount_allocations)) {
          item.line_level_discount_allocations.forEach(allocation => {
            if (allocation?.discount_application?.type === 'discount_code' && 
                typeof allocation.discount_application.title === 'string') {
              const title = allocation.discount_application.title;
              const key = title.toLowerCase();
              if (!codesMap.has(key)) {
                codesMap.set(key, {
                  title: title,
                  dirty: false
                });
              }
            }
          });
        }
      });
    }
    
    // REMOVED: Legacy fallback using discount_codes array
    // This was causing issues because discount_codes includes rejected codes with applicable:false
    // We should ONLY trust discount applications and allocations, not the discount_codes array
    
    return Array.from(codesMap.values());
  }

  // Helper: Create discount pill element
  // Accepts codeObj with { title, dirty } to support optimistic rendering with animate-pulse
  function createDiscountPill(codeObj) {
    // Support both object { title, dirty } and legacy string format
    const code = typeof codeObj === 'string' ? codeObj : codeObj.title;
    const isDirty = typeof codeObj === 'object' && codeObj.dirty === true;
    
    const pill = document.createElement('div');
    // Add animate-pulse class for optimistic (dirty) codes
    const baseClasses = 'bg-[#F2F2F2] md:bg-white box-border flex gap-3 items-center px-1.5 py-0.5 rounded-full';
    pill.className = isDirty ? `${baseClasses} animate-pulse` : baseClasses;
    pill.setAttribute('data-discount-code', code);
    if (isDirty) {
      pill.setAttribute('data-dirty', 'true');
    }

    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'flex gap-1 items-center';

    const iconWrapper = document.createElement('div');
    iconWrapper.className = 'h-3.5 w-3.5 flex-shrink-0';
    iconWrapper.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 14 14"><path fill="#000" d="M6.04.116c.477-.155.99-.155 1.467 0l2.72.884c.476.155.891.457 1.186.862l1.68 2.313c.295.405.454.894.454 1.395v2.86l-.001.093a2.38 2.38 0 0 1-.452 1.302l-1.68 2.313-.058.075c-.29.368-.683.642-1.13.787l-2.72.884-.089.027c-.42.119-.866.119-1.287 0l-.09-.027L3.321 13a2.38 2.38 0 0 1-1.13-.787l-.057-.075-1.68-2.313a2.37 2.37 0 0 1-.452-1.302L0 8.43V5.57c0-.47.14-.928.4-1.318l.053-.077 1.68-2.313c.295-.405.711-.707 1.188-.862zm1.231.727a1.6 1.6 0 0 0-.995 0l-2.72.884a1.6 1.6 0 0 0-.804.585l-1.68 2.312a1.6 1.6 0 0 0-.307.946v2.86c0 .34.107.671.306.946l1.681 2.313c.2.274.482.48.805.584l2.72.884c.323.105.671.105.994 0l2.72-.884a1.6 1.6 0 0 0 .804-.585l1.68-2.312c.2-.275.308-.606.308-.946V5.57c0-.34-.108-.671-.307-.946l-1.681-2.312a1.6 1.6 0 0 0-.805-.585zm1.903 3.216a.383.383 0 0 1 .54.54L4.374 9.942a.383.383 0 0 1-.541-.54zM8.6 7.892a.89.89 0 0 1 .844.888v.046a.89.89 0 0 1-.89.845l-.046-.001a.89.89 0 0 1-.843-.844V8.78a.89.89 0 0 1 .889-.89zm-.046.762a.126.126 0 1 0 .126.126.126.126 0 0 0-.126-.126M5.039 4.33c.47.024.845.413.845.89l-.002.046a.89.89 0 0 1-.889.843h-.046a.89.89 0 0 1-.843-.843l-.001-.046a.89.89 0 0 1 .89-.89zm-.046.764a.126.126 0 1 0 0 .252.126.126 0 0 0 0-.252"/></svg>
    `;

    const textWrapper = document.createElement('div');
    textWrapper.className = "flex flex-col font-['Grotek:Medium',sans-serif] justify-end leading-[1.1] text-[10px] text-black whitespace-nowrap";
    const paragraph = document.createElement('p');
    paragraph.textContent = code;
    textWrapper.appendChild(paragraph);

    contentWrapper.append(iconWrapper, textWrapper);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'relative w-2 h-2 flex-shrink-0 appearance-none bg-transparent border-none p-0 cursor-pointer flex items-center justify-center';
    removeButton.setAttribute('data-remove-discount', code);
    removeButton.setAttribute('aria-label', `Remove discount code ${code}`);
    removeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" class="absolute inset-0 text-black">
        <path d="M1 1L7 7M7 1L1 7" stroke="currentColor" stroke-width="1.37" stroke-linecap="round"/>
      </svg>
    `;

    pill.append(contentWrapper, removeButton);
    return pill;
  }

  // Helper: Update discount codes list elements
  // Single render function - loops through discount codes and renders pills
  // If code is dirty (optimistic/unverified), pill gets animate-pulse class
  // If code is not dirty (server-confirmed), pill renders normally
  // If code was removed from cart, pill gets removed
  function updateDiscountCodesListElements(cart, optimistic = true) {
    const codes = extractCartDiscountCodes(cart); // Returns array of { title, dirty }
    
    const listElements = document.querySelectorAll('[data-discount-codes-list]');
    listElements.forEach((listElement) => {
      const shouldUpdateOptimistically =
        listElement.getAttribute('data-update-optimistic') !== 'false';
      if (optimistic === shouldUpdateOptimistically) {
        // Clear existing pills
        listElement.innerHTML = '';
        
        // Render new pills - pass full codeObj with { title, dirty }
        // createDiscountPill will add animate-pulse class if dirty: true
        codes.forEach((codeObj) => {
          listElement.appendChild(createDiscountPill(codeObj));
        });
        
        // Toggle visibility using hidden class
        if (codes.length > 0) {
          listElement.classList.remove('hidden');
        } else {
          listElement.classList.add('hidden');
        }
      }
    });
  }

  // Helper: Update cart visibility for elements
  function updateCartVisibility(cart) {
    const visibilityElements = document.querySelectorAll(
      "form[data-product-id][data-cart-when], form[data-product-id][data-cart-visibility-action]",
    );

    visibilityElements.forEach((element) => {
      const productId = Number(element.getAttribute("data-product-id"));
      const when = element.getAttribute("data-cart-when"); // 'in_cart' | 'not_in_cart'
      const legacyAction = element.getAttribute("data-cart-visibility-action");

      // Find if this product is in the cart
      const cartItem = cart.items.find(
        (item) =>
          Number(item.id) === productId ||
          Number(item.variant_id) === productId,
      );
      const isInCart = !!(cartItem && cartItem.quantity > 0);

      // Determine visibility: only apply cart visibility rules if data-cart-when is explicitly set
      let shouldShow = null; // null means no visibility rules applied
      
      if (when && when !== "false" && when !== "null") {
        shouldShow = when === "in_cart" ? isInCart : !isInCart;
      }
      
      // If no cart visibility rules are set, don't change the element's visibility
      if (shouldShow === null) {
        return; // Skip this element - no visibility rules to apply
      }

      // Legacy fallback mapping
      if (legacyAction) {
        switch (legacyAction) {
          case "show_when_in_cart":
            shouldShow = isInCart;
            break;
          case "hide_when_in_cart":
            shouldShow = !isInCart;
            break;
          case "show_when_not_in_cart":
            shouldShow = !isInCart;
            break;
          case "hide_when_not_in_cart":
            shouldShow = isInCart;
            break;
        }
      }

      // If attributes live on a container (e.g., product form wrapper), toggle actionable children only
      const scopedButtons = element.querySelectorAll('button, [role="button"]');
      const scopedQuantities = element.querySelectorAll("cart-quantity-input");
      const targets =
        scopedButtons.length + scopedQuantities.length > 0
          ? [...scopedButtons, ...scopedQuantities]
          : [element];

      targets.forEach((targetEl) => {
        if (shouldShow) {
          targetEl.style.display = "";
          targetEl.classList.remove("hidden");
          if (targetEl.tagName === "BUTTON") {
            // Check if this button should remain disabled for business logic reasons
            const shouldRemainDisabled = targetEl.hasAttribute('data-out-of-stock') || 
                                        targetEl.hasAttribute('data-variant-unavailable') ||
                                        targetEl.hasAttribute('data-business-disabled');
            
            if (!shouldRemainDisabled) {
              targetEl.disabled = false;
              targetEl.classList.remove("opacity-50", "pointer-events-none");
            }
          }
        } else {
          targetEl.style.display = "none";
          targetEl.classList.add("hidden");
        }
      });
    });
  }

  // Helper: Update cart state visibility for elements
  function updateCartStateVisibility(cart) {
    const stateElements = document.querySelectorAll("[data-cart-state]");
    
    if (stateElements.length === 0) {
      return;
    }
    
    // Get cart state information
    const cartState = getCartState(cart);
    
    stateElements.forEach((element) => {
      const stateAttr = element.getAttribute("data-cart-state"); // 'empty:show', 'full:hide', 'minimum:show', etc.
      
      // Parse the merged attribute format: "state:action"
      const [state, action] = stateAttr.split(":");
      const finalAction = action || "show"; // Default to show if no action specified
      
      // Determine if this element should be visible based on cart state
      let shouldShow = false;
      
      switch (state) {
        case "empty":
          shouldShow = cartState.isEmpty;
          break;
        case "full":
          shouldShow = cartState.isFull;
          break;
        case "filling":
          shouldShow = cartState.isFilling;
          break;
        case "minimum":
          // Check if cart meets minimum threshold using theme settings
          // Uses cart_free_shipping_threshold_type and cart_min_value_for_free_shipping from theme settings
          const thresholdType = window.Shopify?.theme?.settings?.cart_free_shipping_threshold_type || "value";
          
          if (thresholdType === "value") {
            // Value-based threshold from theme settings
            // Convert from pounds to cents
            const globalThresholdPounds = parseFloat(
              window.Shopify?.theme?.settings?.cart_min_value_for_free_shipping || "0"
            );
            const globalThresholdCents = globalThresholdPounds * 100;
            const cartTotal = cart.total_price || 0; // Already in cents
            shouldShow = cartTotal >= globalThresholdCents;
          } else {
            // Item-based threshold from theme settings
            const minValue = parseInt(
              window.Shopify?.theme?.settings?.cart_min_items_per_box || "0",
              10
            );
            shouldShow = cartState.itemCount >= minValue;
          }
          break;
        case "discount":
          // Check if cart has discounts applied
          // Use total_discount (most reliable) or check discount application arrays
          const totalDiscount = cart.total_discount || 0;
          const hasDiscountApplications = (cart.discount_applications && 
            Array.isArray(cart.discount_applications) && 
            cart.discount_applications.length > 0) ||
            (cart.cart_level_discount_applications &&
            Array.isArray(cart.cart_level_discount_applications) &&
            cart.cart_level_discount_applications.length > 0);
          shouldShow = totalDiscount > 0 || hasDiscountApplications;
          break;
        default:
          // Unknown cart state
          return;
      }
      
      // Apply action (show/hide)
      if (finalAction === "hide") {
        shouldShow = !shouldShow;
      }
      
      // Apply visibility using Tailwind classes
      if (shouldShow) {
        element.classList.remove("hidden");
        element.style.display = "";
      } else {
        element.classList.add("hidden");
        element.style.display = "none";
      }
    });
    
    // Also update checkout element states based on cart state
    updateCheckoutElementStates(cartState);
  }

  // Helper: Update checkout element states based on cart state
  function updateCheckoutElementStates(cartState) {
    // Get minimum threshold from theme settings
    const minThreshold = window.Shopify?.theme?.settings?.cart_min_items_per_box || 0;
    const minValue = parseInt(minThreshold, 10);
    
    // Check if cart meets minimum requirement
    const meetsMinimum = cartState.itemCount >= minValue;
    
    // Find all checkout-related elements - more specific selectors to avoid disabling add-to-cart forms
    const checkoutElements = document.querySelectorAll(`
      a[href*="/checkout"],
      a[href*="/cart"][data-checkout-button],
      a[href*="/cart"].checkout-button,
      a[href*="/cart"].cart-checkout,
      form[action*="/checkout"],
      form[action*="/cart"][data-checkout-form],
      form[action*="/cart"].checkout-form,
      button[data-action*="checkout"],
      button[data-checkout-button],
      button.checkout-button,
      button.cart-checkout,
      [data-checkout-button],
      [data-cart-checkout],
      .checkout-button,
      .cart-checkout,
      input[type="submit"][value*="checkout"],
      input[type="submit"][value*="Checkout"],
      input[type="submit"][data-checkout-submit],
      input[type="submit"].checkout-submit
    `);
    
    checkoutElements.forEach((element) => {
      if (element.tagName === 'A') {
        // Handle links
        if (meetsMinimum) {
          // Re-enable checkout links
          element.style.pointerEvents = '';
          element.classList.remove('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
          element.removeAttribute('data-checkout-disabled');
          element.removeAttribute('title');
        } else {
          // Disable checkout links
          element.style.pointerEvents = 'none';
          element.classList.add('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
          element.setAttribute('data-checkout-disabled', 'true');
          element.setAttribute('title', `Minimum ${minValue} item${minValue !== 1 ? 's' : ''} required for checkout`);
        }
      } else if (element.tagName === 'FORM') {
        // Handle forms
        const submitButton = element.querySelector('input[type="submit"], button[type="submit"]');
        if (submitButton) {
          if (meetsMinimum) {
            // Check if this button should remain disabled for business logic reasons
            const shouldRemainDisabled = submitButton.hasAttribute('data-out-of-stock') || 
                                        submitButton.hasAttribute('data-variant-unavailable') ||
                                        submitButton.hasAttribute('data-business-disabled');
            
            if (!shouldRemainDisabled) {
              // Re-enable checkout forms
              submitButton.disabled = false;
              submitButton.classList.remove('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
              submitButton.removeAttribute('data-checkout-disabled');
              submitButton.removeAttribute('title');
              element.removeAttribute('data-checkout-disabled');
            }
          } else {
            // Disable checkout forms
            submitButton.disabled = true;
            submitButton.classList.add('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
            submitButton.setAttribute('data-checkout-disabled', 'true');
            submitButton.setAttribute('title', `Minimum ${minValue} item${minValue !== 1 ? 's' : ''} required for checkout`);
            element.setAttribute('data-checkout-disabled', 'true');
          }
        }
      } else if (element.tagName === 'BUTTON' || element.tagName === 'INPUT') {
        // Handle buttons and inputs
        if (meetsMinimum) {
          // Check if this button should remain disabled for business logic reasons
          const shouldRemainDisabled = element.hasAttribute('data-out-of-stock') || 
                                      element.hasAttribute('data-variant-unavailable') ||
                                      element.hasAttribute('data-business-disabled');
          
          if (!shouldRemainDisabled) {
            // Re-enable checkout buttons/inputs
            element.disabled = false;
            element.classList.remove('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
            element.removeAttribute('data-checkout-disabled');
            element.removeAttribute('title');
          }
        } else {
          // Disable checkout buttons/inputs
          element.disabled = true;
          element.classList.add('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
          element.setAttribute('data-checkout-disabled', 'true');
          element.setAttribute('title', `Minimum ${minValue} item${minValue !== 1 ? 's' : ''} required for checkout`);
        }
      } else {
        // Handle other elements (divs, spans, etc.)
        if (meetsMinimum) {
          // Re-enable checkout elements
          element.classList.remove('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
          element.removeAttribute('data-checkout-disabled');
          element.removeAttribute('title');
        } else {
          // Disable checkout elements
          element.classList.add('opacity-50', 'pointer-events-none', 'cursor-not-allowed');
          element.setAttribute('data-checkout-disabled', 'true');
          element.setAttribute('title', `Minimum ${minValue} item${minValue !== 1 ? 's' : ''} required for checkout`);
        }
      }
    });
    
    // Emit event for checkout state change
    window.dispatchEvent(new CustomEvent('cart:checkout-state-changed', { 
      detail: { 
        meetsMinimum, 
        currentCount: cartState.itemCount, 
        minimumRequired: minValue,
        cartState 
      } 
    }));
  }

  // Helper: Get cart state information
  function getCartState(cart) {
    const itemCount = Array.isArray(cart.items) 
      ? cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
      : 0;
    
    // Get capacity settings from the cart buffer or theme settings
    const capEnabled = window.Shopify?.cart?._capEnabled || false;
    const maxItemsPerBox = window.Shopify?.cart?._maxItemsPerBoxConfig || 
      window.Shopify?.theme?.settings?.cart_max_items_per_box || null;
    
    const isEmpty = itemCount === 0;
    const isFull = capEnabled && maxItemsPerBox && itemCount >= maxItemsPerBox;
    const isFilling = !isEmpty && !isFull;
    
    return {
      isEmpty,
      isFull,
      isFilling,
      itemCount,
      maxItemsPerBox,
      capEnabled
    };
  }

  // Helper: Update product form button states based on processing status
  function updateProductFormButtonStates(isProcessing) {
    // Find all product form buttons and inputs that should be disabled during processing
    // More specific selectors to avoid disabling add-to-cart forms
    const productFormButtons = document.querySelectorAll(`
      [data-product-id] button,
      [data-product-id] input,
      [data-product-id] cart-quantity-input button,
      [data-product-id] cart-quantity-input input,
      form[action*="/cart/add"] button,
      form[action*="/cart/add"] input,
      .product-form button,
      .product-form input
    `);

    productFormButtons.forEach((element) => {
      if (isProcessing) {
        // Disable and add visual feedback
        element.disabled = true;
        element.classList.add('opacity-50', 'pointer-events-none');
        element.setAttribute('data-processing', 'true');
      } else {
        // Check if this button should remain disabled for business logic reasons
        // (e.g., out of stock, variant not selected, etc.)
        const shouldRemainDisabled = element.hasAttribute('data-out-of-stock') || 
                                    element.hasAttribute('data-variant-unavailable') ||
                                    element.hasAttribute('data-business-disabled');
        
        if (!shouldRemainDisabled) {
          // Only re-enable if not disabled for business reasons
          element.disabled = false;
          element.classList.remove('opacity-50', 'pointer-events-none');
          element.removeAttribute('data-processing');
        }
      }
    });

    // Emit processing state change event
    window.dispatchEvent(new CustomEvent('cart:processing-state-changed', { 
      detail: { isProcessing } 
    }));
  }

  // Helper: Check if any cart operations are currently processing
  function isCartProcessing() {
    return window.Shopify?.cart?._syncing || 
           (window.Shopify?.cart?._addDebounceTimers?.size > 0) ||
           (window.Shopify?.cart?._changeDebounceTimers?.size > 0);
  }

  // Helper: Exponential backoff
  function getRetryDelay(attempt) {
    return RETRY_BASE_DELAY * Math.pow(2, attempt);
  }

  // Helper: Save to localStorage
  function saveCartToStorage(cart) {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (e) {}
  }

  // Helper: Load from localStorage
  function loadCartFromStorage() {
    try {
      const data = localStorage.getItem(CART_STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  // Helper: Fetch current cart from Shopify
  async function fetchShopifyCart() {
    const res = await fetch("/cart.js");
    if (!res.ok) throw new Error("Failed to fetch cart");
    return await res.json();
  }

  // Helper: Recalculate item_count
  function recalcItemCount(cart) {
    cart.item_count = Array.isArray(cart.items)
      ? cart.items.reduce((sum, i) => sum + (i.quantity || 0), 0)
      : 0;
  }

  // Helper: Trigger modal by ID
  function triggerModal(modalId) {
    if (!modalId) return false;
    
    const modal = document.querySelector("#" + modalId);
    if (modal && typeof modal.showPopover === "function") {
      modal.showPopover();
        return true;
      } else {
        return false;
      }
  }

  // Helper: Handle redirect after form submission
  function handleFormRedirect(form) {
    if (!form) return;
    
    // Always check if we need to close a dialog first
    const dialog = form.closest('dialog, [popover]');
    
    // Look for redirect URL directly on the form element
    const redirectUrl = form.getAttribute('data-redirect-url');
    
    if (redirectUrl && redirectUrl.trim()) {
      // Extract path from redirect URL (handle both relative and absolute URLs)
      let redirectPath = redirectUrl;
      try {
        // If it's an absolute URL, extract the pathname
        if (redirectUrl.startsWith('http://') || redirectUrl.startsWith('https://')) {
          const url = new URL(redirectUrl);
          redirectPath = url.pathname;
        } else if (redirectUrl.startsWith('/')) {
          // Already a path
          redirectPath = redirectUrl;
        } else {
          // Relative path, make it absolute
          redirectPath = '/' + redirectUrl;
        }
      } catch (e) {
        // If URL parsing fails, use the original value
        redirectPath = redirectUrl;
      }
      
      // Get current page path
      const currentPath = window.location.pathname;
      
      // Check if redirect path matches current path
      if (redirectPath === currentPath) {
        // Same path - no redirect needed, but close dialog if present
        if (dialog.hasAttribute('popover')) {
          dialog.hidePopover();
        } else if (dialog && typeof dialog.close === 'function') {
          dialog.close();
        }
        return;
      }
      
      // Different path - proceed with redirect (dialog will close automatically)
      window.location.href = redirectUrl;
    } else {
      // No redirect URL configured - just close dialog if present
      if (dialog && typeof dialog.close === 'function') {
        dialog.close();
      }
    }
  }

  // Cart Buffer Singleton
  const OptimisticCart = {
    _cart: null, // Local buffer
    _queue: [], // Pending actions (for offline)
    _syncing: false,
    _init: false,
    _addDebounceTimers: new Map(), // Track debounce timers for each item
    _addDebounceDelay: 300, // ms to wait before processing batched adds
    _addDebounceQuantities: new Map(), // Track accumulated quantities for each item
    _changeDebounceTimers: new Map(), // Track debounce timers for change operations (legacy - being phased out)
    _changeDebounceDelay: 300, // ms to wait before processing batched changes
    
    // New queue-based debouncing system (more reliable than setTimeout)
    _changeQueue: new Map(), // Track pending change operations per item: key -> {syncData, timestamp, rafId}
    _changeDebounceMs: 300, // Configurable debounce delay
    _addQueue: new Map(), // Track pending add operations per item: key -> {syncData, timestamp, rafId}
    _addDebounceMs: 300, // Configurable debounce delay for adds

    // Capacity control (driven by theme settings and <cart-block> config)
    _capEnabled: false,
    _maxItemsPerBoxConfig: null,

    // --- Public API ---
    async get() {
      if (!this._cart) await this._ensureCart();
      return deepClone(this._cart);
    },

    async add({ id, quantity = 1, properties = {}, selling_plan, _form }) {
      await this._ensureCart();
      
      // Enforce capacity if configured (looping disabled)
      let requestedQuantity = quantity;
      let actualQuantityToAdd = quantity;
      let shouldEmitMaxItemsError = false;
      
      if (this._capEnabled && this._maxItemsPerBoxConfig) {
        const currentCount = Array.isArray(this._cart.items)
          ? this._cart.items.reduce((s, i) => s + (i.quantity || 0), 0)
          : 0;
        
        // Calculate what can actually be added
        const availableSpace = this._maxItemsPerBoxConfig - currentCount;
        
        if (availableSpace <= 0) {
          // Cart is already at or over capacity - can't add anything
          actualQuantityToAdd = 0;
          shouldEmitMaxItemsError = true;
        } else if (requestedQuantity > availableSpace) {
          // Can only add partial quantity
          actualQuantityToAdd = availableSpace;
          shouldEmitMaxItemsError = true;
        } else {
          // Can add full quantity
          actualQuantityToAdd = requestedQuantity;
        }
        
        // Always emit max items error if we can't add the full requested quantity
        if (shouldEmitMaxItemsError) {
          emit("cart:error", { error: { message: "max_items_reached" }, cart: this._cart });
          
          // Show modal instead of redirecting when cart is full
          const modalId = window.Shopify?.theme?.settings?.modal_trigger_id;
          if (modalId && window.location.pathname !== '/cart') {
            triggerModal(modalId);
          }
          
          // Don't redirect when cart is full - just show the modal
          return;
        }
        
        // If we can't add anything, don't proceed with any operations
        if (actualQuantityToAdd === 0) {
          return;
        }
      }
      // Normalize id to number for comparison
      const normId = Number(id);
      const normProps =
        properties && typeof properties === "object" ? properties : {};

      // Create a unique key for this item combination
      const itemKey = this._getItemKey(normId, normProps, selling_plan);

      // Accumulate quantity for this item (for batching multiple rapid add clicks)
      const currentAccumulated = this._addDebounceQuantities.get(itemKey) || 0;
      let newAccumulated = currentAccumulated + actualQuantityToAdd;
      
      this._addDebounceQuantities.set(itemKey, newAccumulated);

      // Only update optimistically if we have a valid quantity to add
      if (actualQuantityToAdd > 0) {
        // Optimistically update buffer immediately for UI responsiveness
        this._optimisticAdd({
          id: normId,
          quantity: actualQuantityToAdd,
          properties: normProps,
          selling_plan,
        });
      }

      // Queue the add operation using RAF-based debouncing
      const syncData = {
        id: normId,
        properties: normProps,
        selling_plan,
        _form,
        itemKey, // Pass itemKey so we can retrieve accumulated quantity later
      };
      
      this._queueAddOperation(itemKey, syncData);
    },

    async update({ updates, note, attributes, discount, sections }) {
      await this._ensureCart();
      
      
      // Check capacity if configured when updating quantities
      if (this._capEnabled && this._maxItemsPerBoxConfig && updates) {
        const currentCount = Array.isArray(this._cart.items)
          ? this._cart.items.reduce((s, i) => s + (i.quantity || 0), 0)
          : 0;
        
        // Calculate what the new total would be
        let newTotal = currentCount;
        for (const key in updates) {
          const item = this._cart.items.find(
            (i) => i.key === key || i.id === Number(key),
          );
          if (item) {
            const currentQty = item.quantity || 0;
            const newQty = updates[key];
            newTotal = newTotal - currentQty + newQty;
          }
        }
        
        // If the new total would exceed the limit, don't proceed
        if (newTotal > this._maxItemsPerBoxConfig) {
          emit("cart:error", { error: { message: "max_items_reached" }, cart: this._cart });
          return; // Block the update completely
        }
      }
      
      // Optimistic: update quantities
      const optimisticCart = deepClone(this._cart);
      if (updates) {
        for (const key in updates) {
          const item = optimisticCart.items.find(
            (i) => i.key === key || i.id === Number(key),
          );
          if (item) item.quantity = updates[key];
        }
      }
      if (note !== undefined) optimisticCart.note = note;
      if (attributes)
        optimisticCart.attributes = {
          ...optimisticCart.attributes,
          ...attributes,
        };
      
      // Handle discount code optimistically with dirty flag
      if (discount !== undefined) {
        optimisticCart.discount = discount;
        
        // Parse the discount codes from the comma-separated string
        const requestedCodes = discount ? discount.split(',').map(c => c.trim()).filter(Boolean) : [];
        
        // Get existing non-discount-code applications (keep these)
        const otherApplications = Array.isArray(optimisticCart.cart_level_discount_applications)
          ? optimisticCart.cart_level_discount_applications.filter(app => app.type !== 'discount_code')
          : [];
        
        // Get current verified discount code titles (without dirty ones)
        const existingVerifiedCodes = Array.isArray(optimisticCart.cart_level_discount_applications)
          ? optimisticCart.cart_level_discount_applications
              .filter(app => app.type === 'discount_code' && !app.dirty)
              .map(app => app.title)
              .filter(Boolean)
          : [];
        
        // Rebuild cart_level_discount_applications:
        // 1. Start with non-discount-code applications
        const newApplications = [...otherApplications];
        
        // 2. Add requested codes (either verified or new with dirty flag)
        requestedCodes.forEach(code => {
          // Check if this code is already verified (case-insensitive)
          const isVerified = existingVerifiedCodes.some(
            verified => verified.toLowerCase() === code.toLowerCase()
          );
          
          if (isVerified) {
            // Keep the verified code (find original to preserve all properties)
            const verifiedApp = optimisticCart.cart_level_discount_applications.find(
              app => app.type === 'discount_code' && 
                     app.title && 
                     app.title.toLowerCase() === code.toLowerCase() &&
                     !app.dirty
            );
            if (verifiedApp) {
              newApplications.push(verifiedApp);
            }
          } else {
            // New code - add with dirty flag for optimistic rendering
            newApplications.push({
              type: 'discount_code',
              title: code,
              dirty: true  // Client-side flag for optimistic rendering
            });
          }
        });
        
        // 3. Codes NOT in requestedCodes are automatically excluded (removed)
        optimisticCart.cart_level_discount_applications = newApplications;
      }
      
      if (sections !== undefined) optimisticCart.sections = sections;
      recalcItemCount(optimisticCart);
      this._setCart(optimisticCart, true);
      return this._sync("update", { updates, note, attributes, discount, sections });
    },

    async change({ id, line, quantity, properties, selling_plan }) {
      await this._ensureCart();
      // Enforce capacity if configured when increasing quantities
      let finalQuantity = quantity;
      if (this._capEnabled && this._maxItemsPerBoxConfig && typeof quantity === "number") {
        const optimisticCart = deepClone(this._cart);
        // Determine current quantity of the targeted item
        let currentQty = 0;
        if (id) {
          const item = optimisticCart.items.find(
            (i) => i.key === id || i.id === Number(id) || i.variant_id === Number(id),
          );
          currentQty = item ? (item.quantity || 0) : 0;
        } else if (line !== undefined) {
          const item = optimisticCart.items[line - 1];
          currentQty = item ? (item.quantity || 0) : 0;
        }
        const totalWithoutLine = optimisticCart.items.reduce((s, it) => s + (it.quantity || 0), 0) - currentQty;
        const allowedForLine = Math.max(0, this._maxItemsPerBoxConfig - totalWithoutLine);
        // When looping is disabled, NEVER allow exceeding the limit
        if (quantity > allowedForLine) {
          emit("cart:error", { error: { message: "max_items_reached" }, cart: this._cart });
          
          // Show modal when looping is disabled and capacity is exceeded
          if (!this._spacesEnableLooping) {
            const modalId = window.Shopify?.theme?.settings?.modal_trigger_id;
            if (modalId && window.location.pathname !== '/cart') {
              triggerModal(modalId);
            }
          }
          
          finalQuantity = allowedForLine; // Cap at the maximum allowed
        }
      }

      // Clear any pending debounced operations for this item
      if (id) {
        this._clearPendingDebouncedForItem(id, properties, selling_plan);
      }

      // Find the correct line key and line number for this item
      let lineKey = null;
      let lineNumber = null;
      if (id) {
        const itemIndex = this._cart.items.findIndex(
          (i) => i.id === Number(id) || i.variant_id === Number(id) || i.key === String(id),
        );
        if (itemIndex !== -1) {
          const item = this._cart.items[itemIndex];
          if (item && item.key) {
            lineKey = item.key;
          }
          // Calculate line number (1-indexed for Shopify API)
          lineNumber = itemIndex + 1;
        }
      } else if (line !== undefined) {
        // Line number was provided directly
        lineNumber = line;
      }

      // Create a unique key for this change operation
      const changeKey = lineKey
        ? `change:${lineKey}`
        : lineNumber
          ? `change:line:${lineNumber}`
          : id
            ? `change:${id}`
            : `change:line:${line}`;

      // Clear existing timer for this change
      if (this._changeDebounceTimers.has(changeKey)) {
        const existingTimer = this._changeDebounceTimers.get(changeKey);
        clearTimeout(existingTimer);
      }

      // Only update optimistically if we have a valid quantity
      if (typeof finalQuantity === "number" && finalQuantity >= 0) {
        // Optimistically update buffer immediately for UI responsiveness
        this._optimisticChange({ id, line, quantity: finalQuantity, properties, selling_plan });
      }

      // Build syncData for the sync operation
      const syncData = {
        quantity: finalQuantity,
        properties,
        selling_plan,
      };
      
      // Prefer line key (string) if available
      if (lineKey) {
        syncData.id = lineKey;
      } 
      // Otherwise use line number (1-indexed)
      else if (lineNumber) {
        syncData.line = lineNumber;
      }
      // Fallback: if id is a string (likely already a line key), use it
      else if (id && typeof id === 'string') {
        syncData.id = id;
      }
      // Last resort: if we have an id but it's a number, try to find the line
      else if (id && typeof id === 'number') {
        // Try to find the item in current cart state
        const itemIndex = this._cart.items.findIndex(
          (i) => i.id === Number(id) || i.variant_id === Number(id),
        );
        if (itemIndex !== -1) {
          syncData.line = itemIndex + 1;
        } else {
          return; // Don't sync if we can't identify the item
        }
      }
      
      // Queue-based debouncing (more reliable than setTimeout)
      this._queueChangeOperation(changeKey, syncData);
    },

    async remove(id) {
      await this._ensureCart();

      // Clear any pending debounced operations for this item
      this._clearPendingDebouncedForItem(id);

      // Find the correct line key and line number for this item
      let lineKey = null;
      let lineNumber = null;
      const itemIndex = this._cart.items.findIndex(
        (i) => i.id === Number(id) || i.variant_id === Number(id) || i.key === String(id),
      );
      
      if (itemIndex !== -1) {
        const item = this._cart.items[itemIndex];
        if (item && item.key) {
          lineKey = item.key;
        }
        // Calculate line number (1-indexed for Shopify API)
        lineNumber = itemIndex + 1;
      } else {
        return; // Don't proceed if item not found
      }

      // Optimistically update buffer immediately for UI responsiveness
      this._optimisticRemove(id);

      // Build syncData for removal (quantity: 0)
      const syncData = { quantity: 0 };
      
      // Prefer line key (string) if available
      if (lineKey) {
        syncData.id = lineKey;
      } 
      // Otherwise use line number (1-indexed)
      else if (lineNumber) {
        syncData.line = lineNumber;
      }
      // Fallback: if id is a string (likely already a line key), use it
      else if (id && typeof id === 'string') {
        syncData.id = id;
      }
      else {
        return;
      }
      
      // Remove operations should be IMMEDIATE - no debouncing
      // Users expect instant removal, and there's no reason to batch removes
      this._sync("change", syncData);
    },

    async clear() {
      await this._ensureCart();
      // Clear any pending debounced operations
      this._clearPendingDebounced();
      const optimisticCart = deepClone(this._cart);
      optimisticCart.items = [];
      recalcItemCount(optimisticCart);
      this._setCart(optimisticCart, true);
      this._sync("clear", {});
    },

    // Force process any pending debounced adds
    async flushPendingAdds() {
      // Legacy timer cleanup
      for (const [itemKey, timer] of this._addDebounceTimers) {
        clearTimeout(timer);
        this._addDebounceTimers.delete(itemKey);
      }
      
      // Process all pending add operations in the new queue
      const addPromises = [];
      for (const [itemKey, operation] of this._addQueue.entries()) {
        if (operation.rafId) {
          cancelAnimationFrame(operation.rafId);
        }
        
        const quantity = this._addDebounceQuantities.get(itemKey) || 0;
        this._addDebounceQuantities.delete(itemKey);
        
        if (quantity > 0) {
          // Check capacity before processing pending adds
          if (this._capEnabled && this._maxItemsPerBoxConfig) {
            const currentCount = Array.isArray(this._cart.items)
              ? this._cart.items.reduce((s, i) => s + (i.quantity || 0), 0)
              : 0;
            
            // If adding this quantity would exceed the limit, skip it
            if (currentCount + quantity > this._maxItemsPerBoxConfig) {
              continue;
            }
          }
          
          addPromises.push(
            this._sync("add", {
              id: operation.syncData.id,
              quantity,
              properties: operation.syncData.properties,
              selling_plan: operation.syncData.selling_plan,
              _form: operation.syncData._form,
            })
          );
        }
      }
      this._addQueue.clear();
      
      // Wait for all operations to complete
      if (addPromises.length > 0) {
        await Promise.all(addPromises);
      }
    },

    // Force process any pending debounced changes
    async flushPendingChanges() {
      
      // Legacy timer cleanup
      for (const [changeKey, timer] of this._changeDebounceTimers) {
        clearTimeout(timer);
        this._changeDebounceTimers.delete(changeKey);
      }
      
      // Process all pending change operations in the new queue
      const changePromises = [];
      for (const [changeKey, operation] of this._changeQueue.entries()) {
        if (operation.rafId) {
          cancelAnimationFrame(operation.rafId);
        }
        changePromises.push(this._sync("change", operation.syncData));
      }
      this._changeQueue.clear();
      
      // Wait for all operations to complete
      if (changePromises.length > 0) {
        await Promise.all(changePromises);
      }
    },

    // Force process all pending debounced operations
    async flushPendingOperations() {
      await this.flushPendingAdds();
      await this.flushPendingChanges();
    },

    // Force sync with Shopify cart (useful for debugging or manual sync)
    async forceSyncWithShopify() {
      try {
        const shopifyCart = await fetchShopifyCart();

        this._cart = shopifyCart;
        this._enforceCartCapacity(); // Enforce capacity after sync
        saveCartToStorage(this._cart);
        emit("cart:updated", { cart: this._cart, optimistic: false });
      } catch (error) {
        console.error("Failed to force sync with Shopify:", error);
      }
    },

    // Check if cart is currently processing any operations
    isProcessing() {
      return isCartProcessing();
    },

    // Check if there are any pending operations (debounced or syncing)
    hasPendingOperations() {
      return this._syncing || 
             this._addDebounceTimers.size > 0 || 
             this._changeDebounceTimers.size > 0;
    },

    // Manually disable/enable all product form buttons
    setProductFormButtonsEnabled(enabled) {
      updateProductFormButtonStates(!enabled);
    },

    // Disable/enable buttons for a specific product variant
    setVariantButtonsEnabled(variantId, enabled) {
      const variantButtons = document.querySelectorAll(`
        [data-product-id="${variantId}"] button,
        [data-product-id="${variantId}"] input,
        [data-variant-id="${variantId}"] button,
        [data-variant-id="${variantId}"] input,
        cart-quantity-input[data-variant-id="${variantId}"] button,
        cart-quantity-input[data-variant-id="${variantId}"] input
      `);
      
      variantButtons.forEach((element) => {
        if (enabled) {
          // Check if this button should remain disabled for business logic reasons
          const shouldRemainDisabled = element.hasAttribute('data-out-of-stock') || 
                                      element.hasAttribute('data-variant-unavailable') ||
                                      element.hasAttribute('data-business-disabled');
          
          if (!shouldRemainDisabled) {
            element.disabled = false;
            element.classList.remove('opacity-50', 'pointer-events-none');
            element.removeAttribute('data-processing');
          }
        } else {
          element.disabled = true;
          element.classList.add('opacity-50', 'pointer-events-none');
          element.setAttribute('data-processing', 'true');
        }
      });
    },

    // Check if a specific variant is currently being processed
    isVariantProcessing(variantId) {
      // Check if this variant has any pending operations
      for (const [itemKey, timer] of this._addDebounceTimers) {
        const [idStr] = itemKey.split(":");
        if (Number(idStr) === Number(variantId)) {
          return true;
        }
      }
      
      for (const [changeKey, timer] of this._changeDebounceTimers) {
        if (changeKey.includes(`:${variantId}`) || changeKey.includes(`change:${variantId}`)) {
          return true;
        }
      }
      
      return false;
    },

    // Debug method to get current processing state
    getProcessingState() {
      return {
        syncing: this._syncing,
        addDebounceTimers: this._addDebounceTimers.size,
        changeDebounceTimers: this._changeDebounceTimers.size,
        addDebounceQuantities: this._addDebounceQuantities.size,
        hasPendingOperations: this.hasPendingOperations(),
        isProcessing: this.isProcessing()
      };
    },

    // Manually refresh button states (useful for debugging)
    refreshButtonStates() {
      updateProductFormButtonStates(this.isProcessing());
    },

    // Ensure cart is properly initialized and synced
    async ensureInitialized() {
      await this._ensureCart();
      // If we have pending debounced operations, process them
      if (
        this._addDebounceTimers.size > 0 ||
        this._changeDebounceTimers.size > 0
      ) {
        await this.flushPendingOperations();
      }
      return this._cart;
    },

    // Clear all pending debounced operations
    _clearPendingDebounced() {
      for (const timer of this._addDebounceTimers.values()) {
        clearTimeout(timer);
      }
      for (const timer of this._changeDebounceTimers.values()) {
        clearTimeout(timer);
      }
      this._addDebounceTimers.clear();
      this._addDebounceQuantities.clear();
      this._changeDebounceTimers.clear();
    },

    // Clear pending debounced operations for a specific item
    _clearPendingDebouncedForItem(id, properties = {}, selling_plan) {
      const itemKey = this._getItemKey(Number(id), properties, selling_plan);
      
      // Clear legacy ADD timers
      if (this._addDebounceTimers.has(itemKey)) {
        clearTimeout(this._addDebounceTimers.get(itemKey));
        this._addDebounceTimers.delete(itemKey);
        this._addDebounceQuantities.delete(itemKey);
      }
      
      // Clear ADD queue entries
      if (this._addQueue.has(itemKey)) {
        const operation = this._addQueue.get(itemKey);
        if (operation.rafId) {
          cancelAnimationFrame(operation.rafId);
        }
        this._addQueue.delete(itemKey);
        this._addDebounceQuantities.delete(itemKey);
      }
      
      // Clear CHANGE queue entries (find all keys that match this item)
      const keysToDelete = [];
      for (const [changeKey, operation] of this._changeQueue.entries()) {
        // Check if this changeKey contains the id or itemKey
        if (changeKey.includes(String(id)) || changeKey.includes(itemKey)) {
          if (operation.rafId) {
            cancelAnimationFrame(operation.rafId);
          }
          keysToDelete.push(changeKey);
        }
      }
      
      keysToDelete.forEach(key => {
        this._changeQueue.delete(key);
      });
    },

    /**
     * Queue-based debouncing for add operations (more reliable than setTimeout)
     * This uses requestAnimationFrame + timestamp checking to ensure operations execute
     */
    _queueAddOperation(itemKey, syncData) {
      // Cancel any existing queued operation for this item
      if (this._addQueue.has(itemKey)) {
        const existing = this._addQueue.get(itemKey);
        if (existing.rafId) {
          cancelAnimationFrame(existing.rafId);
        }
      }
      
      const timestamp = performance.now();
      const targetTime = timestamp + this._addDebounceMs;
      
      // Store the operation in the queue
      const operation = {
        syncData,
        timestamp,
        targetTime,
        rafId: null
      };
      
      this._addQueue.set(itemKey, operation);
      
      // Schedule the flush check using RAF
      this._scheduleAddFlush(itemKey);
    },

    /**
     * Schedule an add flush check using requestAnimationFrame
     */
    _scheduleAddFlush(itemKey) {
      const checkAndFlush = () => {
        const operation = this._addQueue.get(itemKey);
        
        // Operation may have been cancelled or already executed
        if (!operation) {
          return;
        }
        
        const now = performance.now();
        const timeRemaining = operation.targetTime - now;
        
        if (timeRemaining <= 0) {
          // Time's up - execute the operation
          this._addQueue.delete(itemKey);
          
          // Retrieve the accumulated quantity for this item
          const finalQuantity = this._addDebounceQuantities.get(itemKey) || 0;
          this._addDebounceQuantities.delete(itemKey);
          
          // Only sync if we have a valid quantity to add
          if (finalQuantity > 0) {
            this._sync("add", {
              id: operation.syncData.id,
              quantity: finalQuantity,
              properties: operation.syncData.properties,
              selling_plan: operation.syncData.selling_plan,
              _form: operation.syncData._form,
            });
          }
        } else {
          // Not ready yet - schedule another check
          operation.rafId = requestAnimationFrame(checkAndFlush);
          this._addQueue.set(itemKey, operation);
        }
      };
      
      // Start the RAF loop
      const operation = this._addQueue.get(itemKey);
      if (operation) {
        operation.rafId = requestAnimationFrame(checkAndFlush);
        this._addQueue.set(itemKey, operation);
      }
    },

    /**
     * Queue-based debouncing for change operations (more reliable than setTimeout)
     * This uses requestAnimationFrame + timestamp checking to ensure operations execute
     */
    _queueChangeOperation(changeKey, syncData) {
      // Cancel any existing queued operation for this item
      if (this._changeQueue.has(changeKey)) {
        const existing = this._changeQueue.get(changeKey);
        if (existing.rafId) {
          cancelAnimationFrame(existing.rafId);
        }
      }
      
      const timestamp = performance.now();
      const targetTime = timestamp + this._changeDebounceMs;
      
      // Store the operation in the queue
      const operation = {
        syncData,
        timestamp,
        targetTime,
        rafId: null
      };
      
      this._changeQueue.set(changeKey, operation);
      
      // Schedule the flush check using RAF (more reliable than setTimeout)
      this._scheduleQueueFlush(changeKey);
    },

    /**
     * Schedule a flush check using requestAnimationFrame
     * This recursively checks if enough time has passed and executes when ready
     */
    _scheduleQueueFlush(changeKey) {
      const checkAndFlush = () => {
        const operation = this._changeQueue.get(changeKey);
        
        // Operation may have been cancelled or already executed
        if (!operation) {
          return;
        }
        
        const now = performance.now();
        const timeRemaining = operation.targetTime - now;
        
        if (timeRemaining <= 0) {
          // Time's up - execute the operation
          this._changeQueue.delete(changeKey);
          this._sync("change", operation.syncData);
        } else {
          // Not ready yet - schedule another check
          operation.rafId = requestAnimationFrame(checkAndFlush);
          this._changeQueue.set(changeKey, operation);
        }
      };
      
      // Start the RAF loop
      const operation = this._changeQueue.get(changeKey);
      if (operation) {
        operation.rafId = requestAnimationFrame(checkAndFlush);
        this._changeQueue.set(changeKey, operation);
      }
    },

    // Public method to clear pending debounced operations for a specific item
    clearPendingForItem(id, properties = {}, selling_plan) {
      this._clearPendingDebouncedForItem(id, properties, selling_plan);
    },

    // Public method to manually update cart state visibility
    updateCartStateVisibility() {
      if (this._cart) {
        updateCartStateVisibility(this._cart);
      }
    },

    // Public method to get current cart state information
    getCartState() {
      if (!this._cart) return null;
      return getCartState(this._cart);
    },

    // Public method to manually update cart attribute values
    updateCartAttributeValues() {
      updateCartAttributeValues();
    },

    // Public method to manually schedule next cart attribute update
    scheduleNextCartAttributeUpdate() {
      scheduleNextCartAttributeUpdate();
    },

    // Public method to manually enforce cart capacity
    enforceCartCapacity() {
      this._enforceCartCapacity();
    },

    // Public method to manually trigger modal
    triggerModal(modalId) {
      const id = modalId || window.Shopify?.theme?.settings?.modal_trigger_id;
      if (id) {
        return triggerModal(id);
      }
      return false;
    },

    // Initialize capacity settings from theme settings
    _initializeCapacityFromThemeSettings() {
      const themeSettings = window.Shopify?.theme?.settings;
      if (themeSettings) {
        // Set max items per box from theme settings
        if (typeof themeSettings.cart_max_items_per_box === "number") {
          // If set to 0, treat as disabled for capacity gating
          const maxCfg = themeSettings.cart_max_items_per_box;
          this._maxItemsPerBoxConfig = maxCfg > 0 ? maxCfg : null;
        }
        
        // Set capacity enabled from theme settings (looping disabled = capacity enabled)
        if (typeof themeSettings.cart_spaces_enable_looping === "boolean") {
          this._capEnabled = themeSettings.cart_spaces_enable_looping === false;
        }

        // If max is disabled (0 => null above), ensure cap is off
        // Also check if both max and min are 0 - if so, fully disable restrictions
        const maxValue = themeSettings.cart_max_items_per_box || 0;
        const minValue = themeSettings.cart_min_items_per_box || 0;
        
        if (this._maxItemsPerBoxConfig == null || (maxValue === 0 && minValue === 0)) {
          this._capEnabled = false;
          this._maxItemsPerBoxConfig = null;
        }
        
        // Set debounce delay from theme settings if available
        if (typeof themeSettings.cart_debounce_delay === "number") {
          this._addDebounceDelay = themeSettings.cart_debounce_delay;
          this._changeDebounceDelay = themeSettings.cart_debounce_delay;
          this._changeDebounceMs = themeSettings.cart_debounce_delay; // For new queue system
          this._addDebounceMs = themeSettings.cart_debounce_delay; // For new add queue system
        }
      }
    },

    // Public method to force refresh cart attribute values and reschedule updates
    refreshCartAttributeValues() {
      updateCartAttributeValues();
      scheduleNextCartAttributeUpdate();
    },

    // Debug method to get current cart attribute calculation info
    getCartAttributeDebugInfo() {
      const globalSettings = window.Shopify?.theme?.settings;
      if (!globalSettings) return { error: 'No theme settings available' };
      
      const timezone = globalSettings.cart_timezone || 'Europe/London';
      
      // Get current time in the target timezone (this is the actual local time there)
      const nowInTimezone = getCurrentTimeInTimezone(timezone);
      
      const deliveryDay = globalSettings.cart_delivery_day !== undefined ? parseInt(globalSettings.cart_delivery_day) : undefined;
      const cutoffDay = globalSettings.cart_cutoff_day !== undefined ? parseInt(globalSettings.cart_cutoff_day) : undefined;
      const cutoffHour = globalSettings.cart_cutoff_hour !== undefined ? parseInt(globalSettings.cart_cutoff_hour) : 0;
      const cutoffMinute = globalSettings.cart_cutoff_minute !== undefined ? parseInt(globalSettings.cart_cutoff_minute) : 0;
      const cutoffSecond = globalSettings.cart_cutoff_second !== undefined ? parseInt(globalSettings.cart_cutoff_second) : 0;
      const cutoffMillisecond = globalSettings.cart_cutoff_millisecond !== undefined ? parseInt(globalSettings.cart_cutoff_millisecond) : 0;
      
      let nextDelivery = null;
      let currentCutoff = null;
      let nextCutoff = null;
      
      if (deliveryDay !== undefined) {
        const currentDay = nowInTimezone.getDay();
        const daysUntilDelivery = (deliveryDay - currentDay + 7) % 7;
        nextDelivery = new Date(nowInTimezone);
        nextDelivery.setDate(nowInTimezone.getDate() + daysUntilDelivery);
        nextDelivery.setHours(0, 0, 0, 0);
        
        if (cutoffDay !== undefined) {
          currentCutoff = new Date(nextDelivery);
          const daysBack = (deliveryDay - cutoffDay + 7) % 7;
          currentCutoff.setDate(nextDelivery.getDate() - daysBack);
          currentCutoff.setHours(cutoffHour, cutoffMinute, cutoffSecond, cutoffMillisecond);
          
          nextCutoff = new Date(currentCutoff);
          nextCutoff.setDate(currentCutoff.getDate() + 7);
          nextCutoff.setHours(cutoffHour, cutoffMinute, cutoffSecond, cutoffMillisecond);
        }
      }
      
      return {
        currentTimeUTC: new Date().toISOString(),
        currentTimeInTimezone: nowInTimezone.toISOString(),
        timezone,
        deliveryDay,
        cutoffDay,
        cutoffTime: `${cutoffHour}:${cutoffMinute}:${cutoffSecond}.${cutoffMillisecond}`,
        nextDelivery: nextDelivery ? nextDelivery.toISOString() : null,
        currentCutoff: currentCutoff ? currentCutoff.toISOString() : null,
        nextCutoff: nextCutoff ? nextCutoff.toISOString() : null,
        cutoffHasPassed: currentCutoff ? nowInTimezone >= currentCutoff : null,
        timeUntilNextUpdate: window.Shopify?.cart?._cartAttributeUpdateTimer ? 'Timer active' : 'No timer'
      };
    },

    // Test method to verify timezone calculations
    testTimezoneCalculation() {
      const globalSettings = window.Shopify?.theme?.settings;
      if (!globalSettings) return { error: 'No theme settings available' };
      
      const timezone = globalSettings.cart_timezone || 'Europe/London';
      const now = new Date();
      const nowInTimezone = getCurrentTimeInTimezone(timezone);
      
      // Test setting a specific time in the target timezone
      const testHour = 17;
      const testMinute = 0;
      const testSecond = 0;
      const testMillisecond = 0;
      
      // Create a date object with the test time in the target timezone
      const testDate = new Date(nowInTimezone);
      testDate.setHours(testHour, testMinute, testSecond, testMillisecond);
      
      return {
        timezone,
        currentTimeUTC: now.toISOString(),
        currentTimeInTimezone: nowInTimezone.toISOString(),
        testTimeInTimezone: testDate.toISOString(),
        testTimeLocal: testDate.toLocaleString(),
        explanation: `Setting time to ${testHour}:${testMinute}:${testSecond}.${testMillisecond} in ${timezone} timezone`
      };
    },

    // Test method to verify cutoff calculation
    testCutoffCalculation() {
      const globalSettings = window.Shopify?.theme?.settings;
      if (!globalSettings) return { error: 'No theme settings available' };
      
      const timezone = globalSettings.cart_timezone || 'Europe/London';
      const deliveryDay = globalSettings.cart_delivery_day !== undefined ? parseInt(globalSettings.cart_delivery_day) : undefined;
      const cutoffDay = globalSettings.cart_cutoff_day !== undefined ? parseInt(globalSettings.cart_cutoff_day) : undefined;
      const cutoffHour = globalSettings.cart_cutoff_hour !== undefined ? parseInt(globalSettings.cart_cutoff_hour) : 0;
      const cutoffMinute = globalSettings.cart_cutoff_minute !== undefined ? parseInt(globalSettings.cart_cutoff_minute) : 0;
      const cutoffSecond = globalSettings.cart_cutoff_second !== undefined ? parseInt(globalSettings.cart_cutoff_second) : 0;
      const cutoffMillisecond = globalSettings.cart_cutoff_millisecond !== undefined ? parseInt(globalSettings.cart_cutoff_millisecond) : 0;
      
      if (deliveryDay === undefined || cutoffDay === undefined) {
        return { error: 'Missing delivery_day or cutoff_day settings' };
      }
      
      const nowInTimezone = getCurrentTimeInTimezone(timezone);
      const currentDay = nowInTimezone.getDay();
      const daysUntilDelivery = (deliveryDay - currentDay + 7) % 7;
      const nextDelivery = new Date(nowInTimezone);
      nextDelivery.setDate(nowInTimezone.getDate() + daysUntilDelivery);
      nextDelivery.setHours(0, 0, 0, 0);
      
      const cutoff = new Date(nextDelivery);
      const daysBack = (deliveryDay - cutoffDay + 7) % 7;
      cutoff.setDate(nextDelivery.getDate() - daysBack);
      cutoff.setHours(cutoffHour, cutoffMinute, cutoffSecond, cutoffMillisecond);
      
      const cutoffHasPassed = nowInTimezone >= cutoff;
      
      return {
        timezone,
        currentTimeInTimezone: nowInTimezone.toISOString(),
        deliveryDay,
        cutoffDay,
        cutoffTime: `${cutoffHour}:${cutoffMinute}:${cutoffSecond}.${cutoffMillisecond}`,
        nextDelivery: nextDelivery.toISOString(),
        cutoff: cutoff.toISOString(),
        cutoffHasPassed,
        daysUntilDelivery,
        daysBack,
        explanation: `Cutoff is ${cutoffHasPassed ? 'PASSED' : 'NOT PASSED'} - Current: ${nowInTimezone.toLocaleString()}, Cutoff: ${cutoff.toLocaleString()}`
      };
    },

    // --- Internal Methods ---
    async _ensureCart() {
      if (this._cart && Array.isArray(this._cart.items)) return;

      // Priority 1: Check window.Shopify.cart (set in theme.liquid) - most up-to-date server-side data
      const windowCart = getInitialCartFromWindow();
      if (windowCart && Array.isArray(windowCart.items)) {
        this._cart = windowCart;
        this._enforceCartCapacity(); // Enforce capacity on window cart
        saveCartToStorage(this._cart);
        return;
      }

      // Priority 2: Try localStorage (cached cart data)
      const stored = loadCartFromStorage();
      if (stored && Array.isArray(stored.items)) {
        this._cart = stored;
        this._enforceCartCapacity(); // Enforce capacity on stored cart
        return;
      }

      // Priority 3: Fallback to fetching from Shopify API
      try {
        const shopifyCart = await fetchShopifyCart();
        if (shopifyCart && Array.isArray(shopifyCart.items)) {
          this._cart = shopifyCart;
          this._enforceCartCapacity(); // Enforce capacity on Shopify cart
          saveCartToStorage(this._cart);
        } else {
          this._cart = { items: [] };
        }
      } catch (e) {
        this._cart = { items: [] };
      }
    },

    // Helper: Enforce cart capacity by reducing quantities if needed
    _enforceCartCapacity() {
      if (!this._capEnabled || !this._maxItemsPerBoxConfig || !this._cart || !Array.isArray(this._cart.items)) {
        return;
      }

      const currentCount = this._cart.items.reduce((s, i) => s + (i.quantity || 0), 0);

      if (currentCount <= this._maxItemsPerBoxConfig) {
        return; // Cart is within capacity, no action needed
      }

      // Calculate how many items need to be removed
      const excessItems = currentCount - this._maxItemsPerBoxConfig;

      // Start reducing from the last item in the cart
      let remainingToReduce = excessItems;
      let itemsModified = [];

      for (let i = this._cart.items.length - 1; i >= 0 && remainingToReduce > 0; i--) {
        const item = this._cart.items[i];
        const currentQty = item.quantity || 0;
        
        if (currentQty > 0) {
          const canReduce = Math.min(currentQty, remainingToReduce);
          const newQty = currentQty - canReduce;
          
          if (newQty === 0) {
            // Remove item completely if quantity becomes 0
            this._cart.items.splice(i, 1);
            itemsModified.push({ action: 'removed', item: { ...item, quantity: currentQty } });
          } else {
            // Reduce quantity
            item.quantity = newQty;
            itemsModified.push({ action: 'reduced', item: { ...item, quantity: currentQty }, newQuantity: newQty });
          }
          
          remainingToReduce -= canReduce;
        }
      }

      // Recalculate item count
      recalcItemCount(this._cart);
      
      // Save the corrected cart
      saveCartToStorage(this._cart);

      // Emit event about the correction
      if (itemsModified.length > 0) {
        emit("cart:capacity-corrected", { 
          originalCount: currentCount, 
          newCount: this._cart.items.reduce((s, i) => s + (i.quantity || 0), 0),
          itemsModified,
          maxCapacity: this._maxItemsPerBoxConfig
        });
        
        // Also emit cart:updated to refresh UI
        emit("cart:updated", { cart: this._cart, optimistic: false });
      }
    },

    _setCart(cart, optimistic = true) {
      this._cart = cart;
      saveCartToStorage(cart);
      
      // IMPORTANT: Price-related values (total, discount, savings) should NEVER be updated optimistically
      // because they depend on server-side calculations (taxes, promotions, rounding, etc.)
      // Only update these after receiving server confirmation (optimistic = false)
      if (!optimistic) {
        // Only update price elements when we have server-confirmed data
        // Pass true so they update elements with default settings (data-update-optimistic not set or "true")
        updateCartTotalElements(cart, true);
        updateCartDiscountTotalElements(cart, true);
        updateCartSavingsElements(cart, true);
      }
      // If optimistic=true, skip price updates entirely - they'll update when server responds
      
      // Count can be updated optimistically since it's simple to calculate
      updateCartCountElements(cart, optimistic);
      
      // CRITICAL: Discount codes should ALWAYS be updated with server-confirmed data
      // because only the server knows if a discount code is valid/applicable
      // We update optimistically for immediate feedback, but ALWAYS update with server data too
      updateDiscountCodesListElements(cart, optimistic);
      if (!optimistic) {
        // Force update discount codes with server data regardless of data-update-optimistic attribute
        // This ensures rejected codes (applicable:false) are removed and valid codes are shown
        updateDiscountCodesListElements(cart, true);
      }
      
      updateCartVisibility(cart); // Update visibility after cart state changes
      updateCartStateVisibility(cart); // Update cart state visibility
      
      // Update button states based on current processing status
      updateProductFormButtonStates(isCartProcessing());
      
      emit("cart:updated", { cart, optimistic });
    },

    _sync(action, data, attempt = 0) {
      if (!navigator.onLine) {
        this._queue.push({ action, data });
        return Promise.resolve(null);
      }
      this._syncing = true;
      
      // Update button states immediately when syncing starts
      updateProductFormButtonStates(true);
      
      let url, body;
      switch (action) {
        case "add":
          url = "/cart/add.js";
          body = {
            items: [
              {
                id: data.id,
                quantity: data.quantity,
                properties: data.properties,
                selling_plan: data.selling_plan,
              },
            ],
          };
          break;
        case "update":
          url = "/cart/update.js";
          body = {};
          if (data.updates) body.updates = data.updates;
          if (data.note !== undefined) body.note = data.note;
          if (data.attributes) body.attributes = data.attributes;
          if (data.discount !== undefined) body.discount = data.discount;
          if (data.sections) body.sections = data.sections;
          break;
        case "change":
          url = "/cart/change.js";
          body = {};
          if (data.id !== undefined) body.id = data.id;
          if (data.line !== undefined) body.line = data.line;
          if (data.quantity !== undefined) body.quantity = data.quantity;
          if (data.properties) body.properties = data.properties;
          if (data.selling_plan !== undefined)
            body.selling_plan = data.selling_plan;
          break;
        case "clear":
          url = "/cart/clear.js";
          body = {};
          break;
        default:
          this._syncing = false;
          updateProductFormButtonStates(isCartProcessing());
          return;
      }

      const request = fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      })
        .then(async (res) => {
          if (!res.ok) {
            const error = await res.json().catch(() => ({}));
            throw error;
          }
          return res.json();
        })
        .then(async (shopifyCart) => {
          if (action === "add") {
            // After add, fetch the full cart to ensure we have the latest state
            const fullCart = await fetchShopifyCart();
            this._setCart(fullCart, false); // canonical update
            
            // Check if we should open modal after every item is added
            // Automatically enabled when both max and min items per box are 0
            const modalId = window.Shopify?.theme?.settings?.modal_trigger_id;
            const maxItemsPerBox = window.Shopify?.theme?.settings?.cart_max_items_per_box || 0;
            const minItemsPerBox = window.Shopify?.theme?.settings?.cart_min_items_per_box || 0;
            const shouldOpenModalAfterEveryAdd = modalId && maxItemsPerBox === 0 && minItemsPerBox === 0;
            
            if (shouldOpenModalAfterEveryAdd && window.location.pathname !== '/cart') {
              // Open modal after every successful add when restrictions are disabled
              // Don't open modal if user is already on the cart page
              triggerModal(modalId);
            } else {
              // Handle redirect if configured (successful add scenario)
              // Only redirect if we have a modal configured and the add was successful
              if (modalId) {
                const form = data._form || document.querySelector(`form[data-product-id="${data.id}"]`) ||
                            document.querySelector(`form[action*="/cart/add"]`);
                if (form) {
                  handleFormRedirect(form);
                }
              }
            }
            return fullCart;
          } else {
            // After change/update/clear, fetch the full cart to preserve discount information
            // The endpoint responses (change.js, update.js, clear.js) don't always include
            // complete discount_applications data, which causes discount codes to disappear from UI
            const fullCart = await fetchShopifyCart();
            this._setCart(fullCart, false); // canonical update
            return fullCart;
          }
        })
        .catch((err) => {
          // For add and change operations, show error alert to user
          if ((action === "add" || action === "change") && attempt === 0) {
            const errorMessage = err.message || err.description || 'An error occurred while updating your cart.';
            alert(errorMessage);
          }
          
          if (attempt < MAX_RETRIES) {
            setTimeout(
              () => this._sync(action, data, attempt + 1),
              getRetryDelay(attempt),
            );
          } else {
            // Rollback: refetch canonical cart
            fetchShopifyCart().then((cart) => {
              this._cart = cart;
              saveCartToStorage(cart);
              emit("cart:error", { error: err, cart });
              emit("cart:updated", { cart });
            });
          }
          return null;
        })
        .finally(() => {
          this._syncing = false;
          // Update button states after syncing completes
          updateProductFormButtonStates(isCartProcessing());
        });

      return request;
    },

    // Multi-tab sync
    _onStorage(e) {
      if (e.key === CART_STORAGE_KEY && e.newValue) {
        try {
          const newCart = JSON.parse(e.newValue);

          this._cart = newCart;
          this._enforceCartCapacity(); // Enforce capacity on multi-tab sync
          emit("cart:updated", { cart: this._cart });
        } catch (err) {}
      }
    },

    // Online event: retry queued actions
    _onOnline() {
      if (this._queue.length) {
        const queue = [...this._queue];
        this._queue = [];
        queue.forEach(({ action, data }) => this._sync(action, data));
      }
    },

    // Init
    _initCartBuffer() {
      if (this._init) return;
      this._init = true;
      
      // Initialize capacity settings from theme settings
      this._initializeCapacityFromThemeSettings();
      
      window.addEventListener("storage", this._onStorage.bind(this));
      window.addEventListener("online", this._onOnline.bind(this));
      // Listen for cart-block config updates for capacity enforcement
      const self = this;
      window.addEventListener("cart-block:spaces", function (e) {
        try {
          const cfg = e && e.detail && e.detail.config;
  
          if (cfg) {
            if (typeof cfg.maxItemsPerBox === "number") {
              self._maxItemsPerBoxConfig = cfg.maxItemsPerBox;

            }
            if (typeof cfg.enableLooping === "boolean") {
              self._capEnabled = cfg.enableLooping === false;

            }
          }
          // Update cart state visibility when capacity configuration changes
          if (self._cart) {
            updateCartStateVisibility(self._cart);
          }
          
          // After setting capacity config, enforce it on the current cart
          if (self._cart && Array.isArray(self._cart.items)) {
            self._enforceCartCapacity();
          }
        } catch (err) {
  
        }
      });
      

      
      // Listen for cart attribute date updates to refresh text elements
      window.addEventListener("cart-attribute:dates-updated", function (e) {
        try {
          const elements = document.querySelectorAll('[data-cart-attribute-value]');
          
          // Cart attribute values are now updated directly in updateCartAttributeValues
          // No need to handle them here since we removed caching
          
          // Also update cart state visibility when dates are updated
          if (self._cart) {
            updateCartStateVisibility(self._cart);
          }
          
          // Also update checkout element states when dates are updated
          if (self._cart) {
            const cartState = getCartState(self._cart);
            updateCheckoutElementStates(cartState);
          }
        } catch (err) {
          // Error updating cart attribute values
        }
      });
      // Update cart attribute values on page load
      updateCartAttributeValues();
      // Cleanup on page unload
      window.addEventListener("beforeunload", () => {
        this.flushPendingOperations();
        // Clear cart attribute update timer
        if (window.Shopify?.cart?._cartAttributeUpdateTimer) {
          clearTimeout(window.Shopify.cart._cartAttributeUpdateTimer);
          window.Shopify.cart._cartAttributeUpdateTimer = null;
        }
      });
      // Enable form interception for zero-config progressive enhancement
      this._interceptForms();
      // Enable button click handling for buttons outside forms
      this._interceptButtons();
    },

    // Intercept button clicks that should submit forms
    _interceptButtons() {
      document.addEventListener("click", (e) => {
        const button = e.target.closest('button[data-submit-closest-product-form="true"], button[for]');
        if (!button) return;
        
        let form = null;
        
        // Check if button is inside an element with data-form-wrapper="true"
        const formWrapper = button.closest('[data-form-wrapper="true"]');
        if (formWrapper) {
          // Find the form that is also a child of this wrapper
          form = formWrapper.querySelector('form');
        }
        
        // Check if button has for attribute
        if (!form) {
          const forAttr = button.getAttribute('for');
          if (forAttr) {
            form = document.getElementById(forAttr);
          }
        }
        
        // Check if button has data-submit-closest-product-form
        if (!form && button.hasAttribute('data-submit-closest-product-form')) {
          // First, try to find form by ID from product-options element
          const productOptions = button.closest('product-options');
          if (productOptions && productOptions.dataset.formId) {
            form = document.getElementById(productOptions.dataset.formId);
          }
          
          // If not found, try closest form
          if (!form) {
          form = button.closest('form');
          }
          
          // If no parent form, look for nearest form with product data
          if (!form) {
            const productForm = button.closest('[data-product-form], [is="product-options"]');
            if (productForm) {
              form = productForm.querySelector('form');
            }
          }
          
          // Fallback: try to find form by common ID
          if (!form) {
            form = document.getElementById('main-product-form');
          }
        }
        
        // Only proceed if we have a form with cart action
        if (!form || !(form instanceof HTMLFormElement)) return;
        
        const action = form.getAttribute("action") || "";
        if (!/\/cart\/(add|change|update|clear)/.test(action)) {
          return;
        }
        
        // Prevent default and trigger form submission
        e.preventDefault();
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    },

    // Intercept Shopify cart forms
    _interceptForms() {
      document.addEventListener("submit", (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        const action = form.getAttribute("action") || "";
        const method = (form.getAttribute("method") || "GET").toUpperCase();
        if (!/\/cart\/(add|change|update|clear)/.test(action)) {
          return;
        }
        // Always intercept cart actions regardless of method to normalize behavior
        e.preventDefault();
        const formData = new FormData(form);
        const actionType = action.match(/\/cart\/(add|change|update|clear)/)[1];
        // Parse form data for each action
        switch (actionType) {
          case "add": {
            // Support multiple items (Shopify supports array syntax)
            const items = [];
            // If form has multiple items[]
            if (formData.has("items[0][id]")) {
              let i = 0;
              while (formData.has(`items[${i}][id]`)) {
                const id = formData.get(`items[${i}][id]`);
                const quantity = Number(
                  formData.get(`items[${i}][quantity]`) || 1,
                );
                // Properties
                const properties = {};
                for (const [key, value] of formData.entries()) {
                  const propMatch = key.match(
                    new RegExp(`items\\[${i}\\]\\[properties\\]\\[(.+)\\]`),
                  );
                  if (propMatch) properties[propMatch[1]] = value;
                }
                items.push({ id, quantity, properties });
                i++;
              }
            } else {
              // Single item
              const id = formData.get("id");
              const quantity = Number(formData.get("quantity") || 1);
              // Properties
              const properties = {};
              for (const [key, value] of formData.entries()) {
                const propMatch = key.match(/^properties\[(.+)\]$/);
                if (propMatch) properties[propMatch[1]] = value;
              }
              items.push({ id, quantity, properties });
            }
            // Call add for each item with form reference for redirect handling
            Promise.all(items.map((item) => this.add({ ...item, _form: form })));
            break;
          }
          case "change": {
            // id or line, quantity, properties
            const id = formData.get("id");
            const line = formData.get("line")
              ? Number(formData.get("line"))
              : undefined;
            const quantity = formData.get("quantity")
              ? Number(formData.get("quantity"))
              : undefined;
            const properties = {};
            for (const [key, value] of formData.entries()) {
              const propMatch = key.match(/^properties\[(.+)\]$/);
              if (propMatch) properties[propMatch[1]] = value;
            }
            this.change({ id, line, quantity, properties });
            break;
          }
          case "update": {
            // updates[]
            const updates = {};
            for (const [key, value] of formData.entries()) {
              const match = key.match(/^updates\[(.+)\]$/);
              if (match) updates[match[1]] = Number(value);
            }
            this.update({ updates });
            break;
          }
          case "clear": {
            this.clear();
            break;
          }
        }
      });
    },

    // Helper: Create unique key for item combination
    _getItemKey(id, properties, selling_plan) {
      const propsStr = JSON.stringify(properties || {});
      const planStr = selling_plan || "";
      return `${id}:${propsStr}:${planStr}`;
    },

    // Helper: Optimistic add without API call
    _optimisticAdd({ id, quantity, properties, selling_plan }) {
      const optimisticCart = deepClone(this._cart);
      // Find matching item (Shopify merges by variant_id and properties)
      let matchIdx = -1;
      for (let i = 0; i < optimisticCart.items.length; i++) {
        const item = optimisticCart.items[i];
        const itemId = Number(item.id);
        const itemVarId = Number(item.variant_id);
        const itemProps =
          item.properties && typeof item.properties === "object"
            ? item.properties
            : {};
        const idMatch = itemId === id || itemVarId === id;
        const propsMatch = deepEqual(itemProps, properties);
        const planMatch =
          item.selling_plan === selling_plan ||
          (!item.selling_plan && !selling_plan);
        if (idMatch && propsMatch && planMatch) {
          matchIdx = i;
          break;
        }
      }
      if (matchIdx > -1) {
        // Increment quantity
        optimisticCart.items[matchIdx].quantity += quantity;
      } else {
        // Try to use an existing item with the same variant as a template
        const template = optimisticCart.items.find(
          (i) => Number(i.id) === id || Number(i.variant_id) === id,
        );
        let newItem;
        // Try to enrich with cached product info
        let cachedInfo = null;
        if (
          window.theme &&
          window.theme.utils &&
          window.theme.utils.productInfoCache
        ) {
          cachedInfo = window.theme.utils.productInfoCache.get(id);
        }
        if (template) {
          newItem = { ...template, quantity, properties, selling_plan };
        } else if (cachedInfo) {
          let productTitle =
            cachedInfo.product && cachedInfo.product.title
              ? cachedInfo.product.title
              : "";
          let variantTitle = "";
          let variantObj = null;
          if (
            cachedInfo.product &&
            Array.isArray(cachedInfo.product.variants)
          ) {
            variantObj = cachedInfo.product.variants.find(
              (v) => Number(v.id) === id,
            );
            if (variantObj) variantTitle = variantObj.title;
          }
          let fullTitle =
            productTitle + (variantTitle ? " - " + variantTitle : "");
          newItem = {
            ...(variantObj || {}),
            id: id,
            variant_id: id,
            quantity,
            properties,
            selling_plan,
            title: fullTitle,
            product_title: productTitle,
            product_handle: cachedInfo.product_handle,
            product_image: cachedInfo.product_image,
            product_url: cachedInfo.product_url,
          };
        } else {
          newItem = {
            id: id,
            variant_id: id,
            quantity,
            properties,
            selling_plan,
          };
        }
        optimisticCart.items.push(newItem);
      }
      recalcItemCount(optimisticCart);
      this._setCart(optimisticCart, true); // optimistic update
    },

    // Helper: Optimistic change without API call
    _optimisticChange({ id, line, quantity, properties, selling_plan }) {
      // Check capacity before any optimistic updates (only for quantity increases)
      if (this._capEnabled && this._maxItemsPerBoxConfig && typeof quantity === "number" && quantity > 0) {
        const currentCount = Array.isArray(this._cart.items)
          ? this._cart.items.reduce((s, i) => s + (i.quantity || 0), 0)
          : 0;
        
        // Determine current quantity of the targeted item
        let currentQty = 0;
        if (id) {
          const item = this._cart.items.find(
            (i) => i.key === id || i.id === Number(id) || i.variant_id === Number(id),
          );
          currentQty = item ? (item.quantity || 0) : 0;
        } else if (line !== undefined) {
          const item = this._cart.items[line - 1];
          currentQty = item ? (item.quantity || 0) : 0;
        }
        
        const totalWithoutLine = currentCount - currentQty;
        const allowedForLine = Math.max(0, this._maxItemsPerBoxConfig - totalWithoutLine);
        
        // If the new quantity would exceed the limit, don't proceed
        if (quantity > allowedForLine) {
          return; // Don't proceed with optimistic update
        }
      }
      
      const optimisticCart = deepClone(this._cart);
      let item;
      if (id) {
        item = optimisticCart.items.find(
          (i) => i.key === id || i.id === Number(id),
        );
      } else if (line !== undefined) {
        item = optimisticCart.items[line - 1];
      }

      if (item) {
        if (quantity === 0) {
          optimisticCart.items = optimisticCart.items.filter((i) => i !== item);
        } else {
          item.quantity = quantity;
          if (properties) item.properties = properties;
          if (selling_plan !== undefined) item.selling_plan = selling_plan;
        }
      }
      recalcItemCount(optimisticCart);
      this._setCart(optimisticCart, true); // optimistic update
    },

    // Helper: Optimistic remove without API call
    _optimisticRemove(id) {
      const optimisticCart = deepClone(this._cart);
      optimisticCart.items = optimisticCart.items.filter(
        (i) => i.id !== Number(id),
      );
      recalcItemCount(optimisticCart);
      this._setCart(optimisticCart, true); // optimistic update
    },
  };

  // Attach to window.Shopify.cart
  // IMPORTANT: Read initial cart data from window.Shopify.cart BEFORE overwriting it
  // This allows theme.liquid to set window.Shopify.cart = {{ cart | json }} for initial cart data
  const initialCartData = (window.Shopify && window.Shopify.cart && Array.isArray(window.Shopify.cart.items))
    ? window.Shopify.cart
    : null;
  
  window.Shopify = window.Shopify || {};
  window.Shopify.cart = OptimisticCart;
  
  // Store initial cart data temporarily so _ensureCart can use it
  if (initialCartData) {
    // Store in a temporary property that getInitialCartFromWindow can access
    // We'll check both window.Shopify._initialCart and window.Shopify.cart (before overwrite)
    window.Shopify._initialCart = initialCartData;
  }
  
  OptimisticCart._initCartBuffer();

        // Try to update cart attribute values immediately if possible
      // This helps avoid showing placeholder text during page load
      try {
        if (document.readyState !== 'loading') {
          // Pre-calculate dates first for faster updates
          // precalculateCartAttributeDates(); // Removed global caching
          updateCartAttributeValues();
          // No cache validation needed - dates calculated fresh
        }
      } catch (err) {
        // Early updateCartAttributeValues failed (expected)
      }

  // Listen for cart updates to keep cart attribute values in sync
  window.addEventListener('cart:updated', () => {
    // Small delay to ensure DOM updates are complete
    setTimeout(() => {
      updateCartAttributeValues();
      // Also update cart state visibility when cart is updated
      if (window.Shopify?.cart?._cart) {
        updateCartStateVisibility(window.Shopify.cart._cart);
      }
    }, 50);
  });

  // Watch for new cart attribute elements being added to the DOM
  if (typeof MutationObserver !== 'undefined') {
    let updateTimeout = null;
    
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node has cart attribute elements
              if (node.hasAttribute && node.hasAttribute('data-cart-attribute-value')) {
                shouldUpdate = true;
              }
              // Check if any children have cart attribute elements
              if (node.querySelectorAll && node.querySelectorAll('[data-cart-attribute-value]').length > 0) {
                shouldUpdate = true;
              }
            }
          });
        }
      });
      
      if (shouldUpdate) {
        // Debounce updates to avoid excessive calls
        if (updateTimeout) {
          clearTimeout(updateTimeout);
        }
        updateTimeout = setTimeout(() => {
          updateCartAttributeValues();
        }, 10);
      }
    });
    
    // Start observing once DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        observer.observe(document.body, { childList: true, subtree: true });
      });
    } else {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // On load, sync buffer with Shopify cart if needed
  // Use DOMContentLoaded to ensure proper initialization order
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      // Pre-calculate dates first for faster updates
      // precalculateCartAttributeDates(); // Removed global caching
      
      // Call updateCartAttributeValues immediately to avoid showing placeholder text
      updateCartAttributeValues();
      
      // No cache validation needed - dates calculated fresh
      
      // Small delay to ensure all elements are rendered
      setTimeout(() => {
        // Priority 1: Check window.Shopify.cart (set in theme.liquid) - most up-to-date server-side data
        const windowCart = getInitialCartFromWindow();
        if (windowCart && Array.isArray(windowCart.items)) {
          // Use window cart if we don't have a cart or if window cart has more items
          if (
            !OptimisticCart._cart ||
            !OptimisticCart._cart.items ||
            windowCart.items.length > OptimisticCart._cart.items.length
          ) {
            OptimisticCart._cart = windowCart;
            saveCartToStorage(windowCart);
            updateCartTotalElements(windowCart, false); // Non-optimistic (server-confirmed data)
            updateCartDiscountTotalElements(windowCart, false); // Non-optimistic (server-confirmed data)
            updateCartSavingsElements(windowCart, false); // Non-optimistic (server-confirmed data)
            updateCartCountElements(windowCart, false); // Non-optimistic (server-confirmed data)
            updateDiscountCodesListElements(windowCart, false); // Non-optimistic (server-confirmed data)
            updateCartVisibility(windowCart); // Update visibility on initial load
            updateCartStateVisibility(windowCart); // Update cart state visibility
            emit("cart:updated", { cart: windowCart });
          } else {
            // Even if we don't update the cart, we should still check visibility
            updateCartVisibility(OptimisticCart._cart || windowCart);
            updateCartStateVisibility(OptimisticCart._cart || windowCart);
          }
          
          // Ensure button states are set correctly on initial load
          updateProductFormButtonStates(isCartProcessing());
          
          // Update cart attribute values again after cart data is loaded
          updateCartAttributeValues();
          return; // Exit early, don't fetch from API
        }
        
        // Priority 2: Fallback to fetching from Shopify API if window.Shopify.cart not available
        fetchShopifyCart()
          .then((cart) => {
            // Only update if we don't have a cart or if the Shopify cart has more items
            if (
              !OptimisticCart._cart ||
              !OptimisticCart._cart.items ||
              cart.items.length > OptimisticCart._cart.items.length
            ) {
          OptimisticCart._cart = cart;
          saveCartToStorage(cart);
          updateCartTotalElements(cart, false); // Non-optimistic (server-confirmed data)
          updateCartDiscountTotalElements(cart, false); // Non-optimistic (server-confirmed data)
          updateCartSavingsElements(cart, false); // Non-optimistic (server-confirmed data)
          updateCartCountElements(cart, false); // Non-optimistic (server-confirmed data)
          updateDiscountCodesListElements(cart, false); // Non-optimistic (server-confirmed data)
          updateCartVisibility(cart); // Update visibility on initial load
              updateCartStateVisibility(cart); // Update cart state visibility
              emit("cart:updated", { cart });
            } else {
              // Even if we don't update the cart, we should still check visibility
              updateCartVisibility(OptimisticCart._cart || cart);
              updateCartStateVisibility(OptimisticCart._cart || cart);
            }
            
            // Ensure button states are set correctly on initial load
            updateProductFormButtonStates(isCartProcessing());
            
            // Update cart attribute values again after cart data is fetched
            updateCartAttributeValues();
          })
          .catch((error) => {
            // Still check visibility even if fetch fails
            if (OptimisticCart._cart) {
              updateCartVisibility(OptimisticCart._cart);
              updateCartStateVisibility(OptimisticCart._cart);
            }
            // Ensure button states are set correctly even if fetch fails
            updateProductFormButtonStates(isCartProcessing());
            
            // Still try to update cart attribute values even if cart fetch fails
            updateCartAttributeValues();
          });
        
        // Update cart attribute values after a longer delay to ensure all scripts have loaded
        setTimeout(() => {
          updateCartAttributeValues();
          // Schedule live updates for cart attribute values
          scheduleNextCartAttributeUpdate();
          
          // Set up fallback periodic check every 5 minutes to ensure system stays in sync
          setInterval(() => {
            updateCartAttributeValues();
            scheduleNextCartAttributeUpdate();
          }, 5 * 60 * 1000); // 5 minutes
        }, 200);
      }, 100); // 100ms delay
    });
  } else {
    // Document already loaded
    // Pre-calculate dates first for faster updates
    // precalculateCartAttributeDates(); // Removed global caching
    
    // Call updateCartAttributeValues immediately to avoid showing placeholder text
    updateCartAttributeValues();
    
    // No cache validation needed - dates calculated fresh
    
    // Small delay to ensure all elements are rendered
    setTimeout(() => {
      fetchShopifyCart()
        .then((cart) => {
          // Only update if we don't have a cart or if the Shopify cart has more items
          if (
            !OptimisticCart._cart ||
            !OptimisticCart._cart.items ||
            cart.items.length > OptimisticCart._cart.items.length
          ) {
            OptimisticCart._cart = cart;
            saveCartToStorage(cart);
            updateCartTotalElements(cart, false); // Non-optimistic (server-confirmed data)
            updateCartDiscountTotalElements(cart, false); // Non-optimistic (server-confirmed data)
            updateCartSavingsElements(cart, false); // Non-optimistic (server-confirmed data)
            updateCartCountElements(cart, false); // Non-optimistic (server-confirmed data)
            updateDiscountCodesListElements(cart, false); // Non-optimistic (server-confirmed data)
            updateCartVisibility(cart); // Update visibility on initial load
            updateCartStateVisibility(cart); // Update cart state visibility
              emit("cart:updated", { cart });
          } else {
            // Even if we don't update the cart, we should still check visibility
            updateCartVisibility(OptimisticCart._cart || cart);
            updateCartStateVisibility(OptimisticCart._cart || cart);
          }
          
          // Ensure button states are set correctly on initial load
          updateProductFormButtonStates(isCartProcessing());
          
          // Update cart attribute values again after cart data is fetched
          updateCartAttributeValues();
        })
        .catch((error) => {
          // Still check visibility even if fetch fails
          if (OptimisticCart._cart) {
            updateCartVisibility(OptimisticCart._cart);
            updateCartStateVisibility(OptimisticCart._cart);
          }
          // Ensure button states are set correctly even if fetch fails
          updateProductFormButtonStates(isCartProcessing());
          
          // Still try to update cart attribute values even if cart fetch fails
          updateCartAttributeValues();
        });
      
      // Update cart attribute values after a longer delay to ensure all scripts have loaded
      setTimeout(() => {
        updateCartAttributeValues();
        // Schedule live updates for cart attribute values
        scheduleNextCartAttributeUpdate();
        
        // Set up fallback periodic check every 5 minutes to ensure system stays in sync
        setInterval(() => {
          updateCartAttributeValues();
          scheduleNextCartAttributeUpdate();
        }, 5 * 60 * 1000); // 5 minutes
      }, 200);
    }, 100); // 100ms delay
  }
})();


